import T from 'utils/T';
import { Canvg } from 'canvg';
import WEEK_DAY_COLUMNS from '../constants/WEEK_DAY_COLUMNS';
import WEEK_ZONE_TYPES from '../constants/WEEK_ZONE_TYPES';
import DAY_ZONE_TYPES from '../constants/DAY_ZONE_TYPES';
import GLOCOSE_COLUMNS from '../constants/GLOCOSE_COLUMNS';
import { numberWithComma } from './number';
import { fromFormat, luxonClone } from './time';
import CHECK_COLORS from 'constants/CHECK_COLORS';
import ABNORMAL_GLOCOSE_TYPES from 'constants/ABNORMAL_GLOCOSE_TYPES';
import ABNORMAL_GLOCOSE_COLORS from 'constants/ABNORMAL_GLOCOSE_COLORS';

const color_callback = (doc, type) => (cell) => {
  if (isNaN(+cell.text)) return;
  const color_type = CHECK_COLORS(type, +cell.text || 0);
  const color = color_type === 'good' ? '#1D82FF' : color_type === 'low' ? '#FF7DFF' : '#FF7D5E';
  doc.setTextColor(color);
};

const W = 1200;

export function ratio(doc) {
  return doc.internal.pageSize.getWidth() / W;
}

export function transform(doc, ...args) {
  const results = args.map(v => v * ratio(doc));
  return args.length === 1 ? results[0] : results;
};

export function pt(doc, pixel) {
  return transform(doc, pixel * 4 / 3);
}

async function addImageSvg(doc, alias, svg, left, top, width, height) {
  const canvas = document.querySelector('canvas');
  canvas.setAttribute('width', width + 'px');
  canvas.setAttribute('height', height + 'px');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const v = Canvg.fromString(ctx, svg);
  v.resize(width * 4, height * 4, 'xMidYMid meet')
  v.render();
  var url = canvas.toDataURL('image/png');
  URL.revokeObjectURL(url);
  await doc.addImage(url, 'PNG', left, top, width, height, alias, 'NONE', 0);
}

async function addImage(doc, alias, url, left, top, width, height) {
  await doc.addImage(url, 'PNG', left, top, width, height, alias, 'NONE', 0);
}

async function vbox({
  paddingLeft = 0,
  paddingRight = 0,
  paddingTop = 0,
  paddingBottom = 0,
  left = 0,
  top = 0,
  width,
  height,
  gap = 0,
}, functions) {
  let totalHeight = paddingTop + paddingBottom - gap;
  top += paddingTop - gap;
  for (let i = 0; i < functions.length; ++i) {
    const func = functions[i];
    if (!func) continue;
    totalHeight += gap;
    top += gap;
    const next = await func(left + paddingLeft, top, width - paddingLeft - paddingRight, height - paddingTop - paddingBottom);
    top = next.nextTop;
    totalHeight += next.height;
  }

  return {
    top,
    totalHeight,
  };
}

async function hbox({
  paddingLeft = 0,
  paddingRight = 0,
  paddingTop = 0,
  paddingBottom = 0,
  left = 0,
  top = 0,
  width,
  height,
  gap = 0,
}, functions) {
  let totalWidth = paddingLeft + paddingRight - gap;
  left += paddingLeft - gap;
  for (let i = 0; i < functions.length; ++i) {
    const func = functions[i];
    if (!func) continue;
    totalWidth += gap;
    left += gap;
    const next = await func(left, top + paddingTop, width - paddingLeft - paddingRight, height - paddingTop - paddingBottom);
    left = next.nextLeft;
    totalWidth += next.width;
  }

  return {
    left,
    totalWidth,
  };
}

function roundedRect(doc, { lineWidth, drawColor, fillColor }, ...args) {
  if (lineWidth) doc.setLineWidth(transform(doc, lineWidth));
  if (drawColor) doc.setDrawColor(drawColor);
  if (fillColor) doc.setFillColor(fillColor);

  const [left, top, width, height] = args;
  const transformed = transform(doc, ...args)
  if (drawColor && fillColor) {
    doc.roundedRect(...transformed, 'DF');
  } else if (drawColor) {
    doc.roundedRect(...transformed, 'S');
  } else if (fillColor) {
    doc.roundedRect(...transformed, 'F');
  }
  return {
    nextLeft: left + width,
    nextTop: top + height,
    width,
    height,
  };
}

export function drawHeader(doc, data) {
  return async (left, top, width, height) => {
    const w = 1120;
    const h = 168;

    const {
      doctor_info,
      patient_no,
      period_start,
      period_end,
      start_time,
      end_time,
      patient_info,
    } = data || {};

    await vbox({
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      paddingBottom: 20,
      top,
      left,
      width: w,
      height: h,
      gap: 10,
    }, [
      async (left, top, width, height) => {
        // WellSee Report
        doc.saveGraphicsState();
        doc.setFontSize(pt(doc, 28));
        doc.setTextColor('#333333');
        doc.setFont('PlusJakartaSansSemiBold', 'normal');
        doc.text('WellSee Report', ...transform(doc, left, top + (35 - 28) / 2), { baseline: 'top', });
        doc.restoreGraphicsState();
        return roundedRect(doc, {}, left, top, 211, 35, 0, 0);
      },
      async (left, top, width, height) => {
        const w = 299;
        const h = 20;
        await hbox({
          left,
          top,
          width: w,
          height: h,
          gap: 40,
        }, [
          async (left, top, width, height) => {
            // 조회기간
            const { totalWidth } = await hbox({
              left,
              top,
              height: 20,
              gap: 5,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 12));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('조회기간');
                const textWidth = doc.getTextWidth(text) / ratio(doc);
                doc.text(text, ...transform(doc, left + (50 - textWidth) / 2, top + (20 - 12) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 50, height, 3, 3);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#667085');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('{0} ~ {1}', start_time, end_time);
                const textWidth = doc.getTextWidth(text) / ratio(doc);
                doc.text(text, ...transform(doc, left, top + (20 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, textWidth, height, 0, 0);
              },
            ]);

            return roundedRect(doc, {}, left, top, totalWidth, height, 0, 0);
          },
          async (left, top, width, height) => {
            // 담당의
            const { totalWidth } = await hbox({
              left,
              top,
              height: 20,
              gap: 5,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 12));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('담당의');
                const textWidth = doc.getTextWidth(text) / ratio(doc);
                doc.text(text, ...transform(doc, left + (39 - textWidth) / 2, top + (20 - 12) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 39, height, 3, 3);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#667085');
                doc.setFont('PretendardRegular', 'normal');
                const text = doctor_info?.name || '';
                const textWidth = doc.getTextWidth(text) / ratio(doc);
                doc.text(text, ...transform(doc, left, top + (20 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, textWidth, height, 0, 0);
              },
            ]);
            return roundedRect(doc, {}, left, top, 250, height, 0, 0);
          },
        ]);

        return roundedRect(doc, {}, left, top, w, h, 0, 0);
      },
      async (left, top, width, height) => {
        // 인슐린 60을 제외한 나머지 항목의 너비
        const itemWidth = (width - 20 - 60) / 7;
        // 환자 정보
        await hbox({
          paddingLeft: 10,
          paddingRight: 10,
          paddingTop: 10,
          paddingBottom: 10,
          left,
          top,
          width: w,
          height: h,
        }, [
          async (left, top, width, height) => {
            // 환자명
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('환자명');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 16));
                doc.setTextColor('#333333');
                doc.setFont('PretendardBold', 'normal');
                doc.text(patient_info?.name || '', ...transform(doc, left, top + (22 - 16) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 22, 0, 0);
              },
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#D8D8D8');
            doc.setLineDashPattern(transform(doc, 2, 2), 0);
            doc.line(...transform(doc, left + itemWidth - 20, top, left + itemWidth - 20, top + 33), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, itemWidth, 33, 0, 0);
          },
          async (left, top, width, height) => {
            // 나이
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('나이');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#333333');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('만 {0} 세', patient_info?.age), ...transform(doc, left, top + (22 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 22, 0, 0);
              },
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#D8D8D8');
            doc.setLineDashPattern(transform(doc, 2, 2), 0);
            doc.line(...transform(doc, left + itemWidth - 20, top, left + itemWidth - 20, top + 33), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, itemWidth, 33, 0, 0);
          },
          async (left, top, width, height) => {
            // 성별
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('성별');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#333333');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(patient_info?.gender || '', ...transform(doc, left, top + (22 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 22, 0, 0);
              },
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#D8D8D8');
            doc.setLineDashPattern(transform(doc, 2, 2), 0);
            doc.line(...transform(doc, left + itemWidth - 20, top, left + itemWidth - 20, top + 33), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, itemWidth, 33, 0, 0);
          },
          async (left, top, width, height) => {
            // 키
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('키');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#333333');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('{0} cm', patient_info?.height || ''), ...transform(doc, left, top + (22 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 22, 0, 0);
              },
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#D8D8D8');
            doc.setLineDashPattern(transform(doc, 2, 2), 0);
            doc.line(...transform(doc, left + itemWidth - 20, top, left + itemWidth - 20, top + 33), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, itemWidth, 33, 0, 0);
          },
          async (left, top, width, height) => {
            // 체중
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('체중');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#333333');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('{0} kg', patient_info?.weight || ''), ...transform(doc, left, top + (22 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 22, 0, 0);
              },
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#D8D8D8');
            doc.setLineDashPattern(transform(doc, 2, 2), 0);
            doc.line(...transform(doc, left + itemWidth - 20, top, left + itemWidth - 20, top + 33), 'S');
            doc.restoreGraphicsState();
            return roundedRect(doc, {}, left, top, itemWidth, 33, 0, 0);
          },
          async (left, top, width, height) => {
            // BMI
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('BMI');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#333333');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(patient_info?.bmi.toString() || '', ...transform(doc, left, top + (22 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 22, 0, 0);
              },
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#D8D8D8');
            doc.setLineDashPattern(transform(doc, 2, 2), 0);
            doc.line(...transform(doc, left + itemWidth - 20, top, left + itemWidth - 20, top + 33), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, itemWidth, 33, 0, 0);
          },
          async (left, top, width, height) => {
            // 당뇨병 종류
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('당뇨병 종류');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#333333');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(patient_info?.dx_diabetes || '', ...transform(doc, left, top + (22 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, itemWidth, 22, 0, 0);
              },
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#D8D8D8');
            doc.setLineDashPattern(transform(doc, 2, 2), 0);
            doc.line(...transform(doc, left + itemWidth - 20, top, left + itemWidth - 20, top + 33), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, itemWidth, 33, 0, 0);
          },
          async (left, top, width, height) => {
            // 인슐린 여부
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                const text = T('인슐린 여부');
                doc.text(text, ...transform(doc, left, top + (13 - 11) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, 60, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 14));
                doc.setTextColor('#333333');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(patient_info?.insulin_inject_yn || '', ...transform(doc, left, top + (22 - 14) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, 60, 22, 0, 0);
              },
            ]);

            return roundedRect(doc, {}, left, top, 60, 33, 0, 0);
          },
        ]);

        doc.saveGraphicsState();
        doc.setLineWidth(transform(doc, 1));
        doc.setDrawColor('#EAEAEA');
        doc.line(...transform(doc, left, top, left + width, top), 'S');
        doc.line(...transform(doc, left, top + 53, left + width, top + 53), 'S');
        doc.saveGraphicsState();
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, width, 53, 0, 0);
      },
    ]);

    // WellSee 이미지
    const position = transform(doc, W - 140, 70, 60, 39);
    addImageSvg(doc, 'logo', `
<svg width="${position[2]}" height="${position[3]}" viewBox="0 0 60 39" fill="white" xmlns="http://www.w3.org/2000/svg">
<g>
<rect x="0" y="0" width="60" height="39" fill="white"/>
<path d="M50.1423 22.4847C51.7802 19.6169 50.8082 15.9499 47.9712 14.2942C45.1343 12.6385 41.5067 13.6211 39.8688 16.4889L32.4991 29.3922C30.8611 32.2599 31.8332 35.9269 34.6701 37.5826C37.5071 39.2383 41.1347 38.2558 42.7726 35.388L50.1423 22.4847Z" fill="#A46DFF"/>
<path d="M11.0703 16.6399C9.43239 13.7721 5.80479 12.7895 2.96783 14.4452C0.130865 16.1009 -0.84115 19.7679 0.79677 22.6357L8.07866 35.3852C9.71658 38.253 13.3442 39.2356 16.1811 37.5799C19.0181 35.9242 19.9901 32.2572 18.3522 29.3894L11.0703 16.6399Z" fill="#C9A9FF"/>
<path d="M31.6499 9.96145C30.012 7.09369 26.3844 6.11112 23.5474 7.76682C20.7105 9.42253 19.7384 13.0895 21.3764 15.9573L32.6344 35.6685C34.2723 38.5362 37.8999 39.5188 40.7369 37.8631C43.5738 36.2074 44.5458 32.5404 42.9079 29.6726L31.6499 9.96145Z" fill="#8148FF"/>
<path d="M30.342 14.4489C31.9799 11.5812 31.0079 7.91418 28.171 6.25848C25.334 4.60277 21.7064 5.58534 20.0685 8.4531L7.95056 29.6698C6.31264 32.5376 7.28466 36.2046 10.1216 37.8603C12.9586 39.516 16.5862 38.5334 18.2241 35.6657L30.342 14.4489Z" fill="#A46DFF"/>
<path d="M54.0182 12.4264C57.3218 12.4264 59.9998 9.71931 59.9998 6.37989C59.9998 3.04048 57.3218 0.333344 54.0182 0.333344C50.7147 0.333344 48.0366 3.04048 48.0366 6.37989C48.0366 9.71931 50.7147 12.4264 54.0182 12.4264Z" fill="#FE728D"/>
</g>
</svg>
`, ...position);
    return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 1120, 168, 10, 10);
  };
}

export function drawPageTitle(doc, title, page, data, dataType) {
  const {
    data_reliability,
    data_reliability_yn,
    meal_cnt,
    meal_day_cnt,
  } = data;
  return async (left, top, width, height) => {
    const pageLeft = left;
    await hbox({
      left,
      top,
      width,
      height,
      gap: 20,
    }, [
      async (left, top, width, height) => {
        doc.saveGraphicsState();
        doc.setFontSize(pt(doc, 24));
        doc.setTextColor('#333333');
        doc.setFont('PretendardSemiBold', 'normal');
        const textWidth = doc.getTextWidth(title) / ratio(doc);
        doc.text(title, ...transform(doc, left, top + (34 - 24) / 2), { align: 'left', baseline: 'top', });
        doc.restoreGraphicsState();

        doc.saveGraphicsState();
        doc.setDrawColor('#333333');
        doc.setFillColor('#333333');
        doc.setLineWidth(transform(doc, 4));
        doc.line(...transform(doc, left, top + 34, left + textWidth, top + 34), 'FD');
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, textWidth, 34, 0, 0);
      },
      async (left, top, width, height) => {
        const blockLeft = left;
        await hbox({
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 10,
          paddingBottom: 10,
          left,
          top,
          width,
          height: 34,
          gap: 10,
        }, [
          async (left, blockTop, width, height) => {
            const top = blockTop + (height - 22) / 2;
            const text = (() => {
              if (dataType === 'diet_data') {
                return T('데이터비율 : {0}건/{1}일', meal_cnt, meal_day_cnt);
              } else {
                return T('데이터비율 : {0}%', data_reliability);
              }
            })();

            doc.saveGraphicsState();
            doc.setFontSize(pt(doc, 10));
            doc.setFont('PretendardSemiBold', 'normal');
            const textWidth = doc.getTextWidth(text) / ratio(doc);
            const totalWidth = 5 + 5 + textWidth + (data_reliability_yn === 'Y' || dataType === 'diet_data' ? 0 : 5 + 67);
            doc.restoreGraphicsState();

            const container = roundedRect(doc, { lineWidth: 1, fillColor: '#E8EDFF' }, blockLeft, top, totalWidth, 22, 6, 6);

            const next = await hbox({
              paddingLeft: 5,
              paddingRight: 5,
              left,
              top,
              width,
              height: 22,
              gap: 5,
            }, [
              async (left, top, width, height) => {
                const next = roundedRect(doc, {}, left, top + (height - 12) / 2, 71, 12, 0, 0);
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 10));
                doc.setTextColor('#667085');
                doc.setFont('PretendardSemiBold', 'normal');
                doc.text(text, ...transform(doc, left, top + (height - 10) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return {
                  ...next,
                  nextLeft: left + textWidth,
                }
              },
              ...(data_reliability_yn === 'Y' || dataType === 'diet_data' ? [] : [
                async (left, top, width, height) => {
                  const next = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top + (height - 18) / 2, 67, 18, 5, 5);
                  doc.saveGraphicsState();
                  doc.setFontSize(pt(doc, 9));
                  doc.setTextColor('#FF995A');
                  doc.setFont('PretendardSemiBold', 'normal');
                  doc.text(T('데이터 불충분'), ...transform(doc, left + 67 - 3, top + (height - 9) / 2), { align: 'right', baseline: 'top', });
                  doc.restoreGraphicsState();
                  // 에러 이미지
                  const position = transform(doc, left + 3, top + (height - 12) / 2, 12, 12);
                  addImageSvg(doc, dataType + page, `
<svg width="${position[2]}" height="${position[3]}" viewBox="0 0 12 12" fill="white" xmlns="http://www.w3.org/2000/svg">
<g>
<rect x="0" y="0" width="12" height="12" fill="#F6F7FB"/>
<path d="M6.5 5V7H5.5V5H6.5Z" fill="#FF995A"/>
<path d="M6.5 8V9H5.5V8H6.5Z" fill="#FF995A"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M6 1L11.5 10.5H0.5L6 1ZM2.23445 9.5L6 2.99586L9.76555 9.5H2.23445Z" fill="#FF995A"/>
</g>
</svg>
`, ...position);
                  return next;
                }
              ])
            ]);

            return {
              ...container,
              width: next.totalWidth,
            };
          },
        ]);

        return roundedRect(doc, {}, left, top, 157, 34, 0, 0);
      },
      async (_, top, width, height) => {
        const left = pageLeft + width - 352;
        await hbox({
          paddingTop: 2,
          paddingBottom: 2,
          left,
          top,
          width,
          height,
          gap: 5,
        }, [
          async (left, top, width, height) => {
            const text = T('혈당');
            const highlight = dataType === 'glocose_data';
            if (highlight) {
              const next = roundedRect(doc, { lineWidth: 1, fillColor: '#333333' }, left, top, 43, 30, 0, 0);
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#FFFFFF');
              doc.setFont('PretendardRegular', 'normal');
              const textWidth = doc.getTextWidth(text) / ratio(doc);
              doc.text(text, ...transform(doc, left + 43 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();

              doc.saveGraphicsState();
              doc.setFillColor('#FFFFFF');
              doc.setLineWidth(transform(doc, 2));
              doc.line(...transform(doc, left + 43 / 2 - textWidth / 2, top + 24, left + 43 / 2 + textWidth / 2, top + 24), 'FD');
              doc.restoreGraphicsState();

              return next;
            } else {
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#AAAAAA');
              doc.setFont('PretendardRegular', 'normal');
              doc.text(text, ...transform(doc, left + 43 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();
              return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 43, 30, 0, 0);
            }
          },
          async (left, top, width, height) => {
            const text = T('식사');
            const highlight = dataType === 'diet_data';
            if (highlight) {
              const next = roundedRect(doc, { lineWidth: 1, fillColor: '#333333' }, left, top, 43, 30, 0, 0);
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#FFFFFF');
              doc.setFont('PretendardRegular', 'normal');
              const textWidth = doc.getTextWidth(text) / ratio(doc);
              doc.text(text, ...transform(doc, left + 43 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();

              doc.saveGraphicsState();
              doc.setFillColor('#FFFFFF');
              doc.setLineWidth(transform(doc, 2));
              doc.line(...transform(doc, left + 43 / 2 - textWidth / 2, top + 24, left + 43 / 2 + textWidth / 2, top + 24), 'FD');
              doc.restoreGraphicsState();

              return next;
            } else {
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#AAAAAA');
              doc.setFont('PretendardRegular', 'normal');
              doc.text(text, ...transform(doc, left + 43 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();
              return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 43, 30, 0, 0);
            }
          },
          async (left, top, width, height) => {
            const text = T('활동(평균걸음수)');
            const highlight = dataType === 'steps_data';
            if (highlight) {
              const next = roundedRect(doc, { lineWidth: 1, fillColor: '#333333' }, left, top, 96, 30, 0, 0);
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#FFFFFF');
              doc.setFont('PretendardRegular', 'normal');
              const textWidth = doc.getTextWidth(text) / ratio(doc);
              doc.text(text, ...transform(doc, left + 96 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();

              doc.saveGraphicsState();
              doc.setFillColor('#FFFFFF');
              doc.setLineWidth(transform(doc, 2));
              doc.line(...transform(doc, left + 96 / 2 - textWidth / 2, top + 24, left + 96 / 2 + textWidth / 2, top + 24), 'FD');
              doc.restoreGraphicsState();

              return next;
            } else {
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#AAAAAA');
              doc.setFont('PretendardRegular', 'normal');
              doc.text(text, ...transform(doc, left + 96 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();
              return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 96, 30, 0, 0);
            }
          },
          async (left, top, width, height) => {
            const text = T('활동(소모칼로리)');
            const highlight = dataType === 'calories_data';
            if (highlight) {
              const next = roundedRect(doc, { lineWidth: 1, fillColor: '#333333' }, left, top, 96, 30, 0, 0);
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#FFFFFF');
              doc.setFont('PretendardRegular', 'normal');
              const textWidth = doc.getTextWidth(text) / ratio(doc);
              doc.text(text, ...transform(doc, left + 96 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();

              doc.saveGraphicsState();
              doc.setFillColor('#FFFFFF');
              doc.setLineWidth(transform(doc, 2));
              doc.line(...transform(doc, left + 96 / 2 - textWidth / 2, top + 24, left + 96 / 2 + textWidth / 2, top + 24), 'FD');
              doc.restoreGraphicsState();

              return next;
            } else {
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#AAAAAA');
              doc.setFont('PretendardRegular', 'normal');
              doc.text(text, ...transform(doc, left + 96 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();
              return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 96, 30, 0, 0);
            }
          },
          async (left, top, width, height) => {
            const text = T('인슐린');
            const highlight = dataType === 'insulin_data';
            if (highlight) {
              const next = roundedRect(doc, { lineWidth: 1, fillColor: '#333333' }, left, top, 54, 30, 0, 0);
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#FFFFFF');
              doc.setFont('PretendardRegular', 'normal');
              const textWidth = doc.getTextWidth(text) / ratio(doc);
              doc.text(text, ...transform(doc, left + 54 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();

              doc.saveGraphicsState();
              doc.setFillColor('#FFFFFF');
              doc.setLineWidth(transform(doc, 2));
              doc.line(...transform(doc, left + 54 / 2 - textWidth / 2, top + 24, left + 54 / 2 + textWidth / 2, top + 24), 'FD');
              doc.restoreGraphicsState();

              return next;
            } else {
              doc.saveGraphicsState();
              doc.setFontSize(pt(doc, 12));
              doc.setTextColor('#AAAAAA');
              doc.setFont('PretendardRegular', 'normal');
              doc.text(text, ...transform(doc, left + 54 / 2, top + (30 - 12) / 2), { align: 'center', baseline: 'top', });
              doc.restoreGraphicsState();
              return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, 54, 30, 0, 0);
            }
          },
        ]);
        return roundedRect(doc, {}, left, top, 352, 34, 0, 0);
      },
    ]);

    return roundedRect(doc, {}, left, top, width, 34, 0, 0);
  };
}

export function drawFooter(doc, title, page, data, dataType) {
  return async (left, _, width, height) => {
    const ratio = doc.internal.pageSize.getWidth() / W;
    const pageHeight = doc.internal.pageSize.getHeight() / ratio;
    const top = pageHeight - 20 - 40;

    doc.saveGraphicsState();
    doc.setFontSize(pt(doc, 11));
    doc.setTextColor('#333333');
    doc.setFont('PretendardRegular', 'normal');
    doc.text(T('{0} {1}', title, page), ...transform(doc, left + 1120 / 2, top + 20 / 2), { align: 'center', baseline: 'middle', });
    doc.restoreGraphicsState();
    return roundedRect(doc, {}, left, top, 1120, 20, 0, 0);
  }
}

export function drawSummaryCaption(doc, data, caption, callback) {
  return async (left, top, width, height) => {
    const next = roundedRect(doc, {}, left, top, width, 14, 0, 0);

    await hbox({
      left,
      top,
      width,
      height: 14,
    }, [
      async (left, top, width, height) => {
        doc.saveGraphicsState();
        doc.setLineWidth(transform(doc, 2));
        doc.setDrawColor('#2E5DCC');
        doc.line(...transform(doc, left, top, left, top + height), 'S');
        doc.restoreGraphicsState();

        doc.saveGraphicsState();
        doc.setFontSize(pt(doc, 13));
        doc.setTextColor('#2E5DCC');
        doc.setFont('PretendardSemiBold', 'normal');
        doc.text(caption, ...transform(doc, left + 10, top + (height - 13) / 2), { baseline: 'top', });
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, width, height, 0, 0);
      },
      callback,
    ]);

    return next;
  }
}

export function drawSummaryBox(doc, data, boxWidth, boxHeight, caption, legendCallback, callbacks, opacity = 0.8) {
  return async (left, top, width, height) => {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ 'stroke-opacity': opacity }));
    const next = roundedRect(doc, { lineWidth: 1, drawColor: '#2E5DCC' }, left, top, boxWidth, boxHeight, 10, 10);
    doc.restoreGraphicsState();

    await vbox({
      paddingLeft: 20,
      paddingRight: 20,
      paddingTop: 20,
      paddingBottom: 20,
      left,
      top,
      width: boxWidth,
      height: boxHeight,
      gap: 20,
    }, [
      drawSummaryCaption(doc, data, caption, legendCallback),
      async (left, top, width, height) => {
        await vbox({
          left,
          top,
          width,
          height,
        }, callbacks);
        return roundedRect(doc, {}, left, top, width, 50, 0, 0);
      },
    ]);
    return next;
  };
}


export function drawSummaryLinkBox(doc, data, boxWidth, boxHeight, caption, legendCallback, callbacks, opacity = 0.6) {
  return async (left, top, width, height) => {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ 'stroke-opacity': opacity }));
    const next = roundedRect(doc, { lineWidth: 1, drawColor: '#2E5DCC' }, left, top, boxWidth, boxHeight, 10, 10);
    doc.restoreGraphicsState();

    await vbox({
      paddingLeft: 20,
      paddingRight: 20,
      paddingTop: 20,
      paddingBottom: 20,
      left,
      top,
      width: boxWidth,
      height: boxHeight,
      gap: 20,
    }, [
      drawSummaryCaption(doc, data, caption, legendCallback),
      ...callbacks,
    ]);
    return next;
  }
}


export function drawSectionCaption(doc, data, caption, callback) {
  return async (left, top, width, height) => {
    const next = roundedRect(doc, {}, left, top, width, 32, 0, 0);

    await hbox({
      left,
      top,
      width,
      height: 32,
    }, [
      async (left, top, width, height) => {
        doc.saveGraphicsState();
        doc.setFontSize(pt(doc, 16));
        doc.setTextColor('#333333');
        doc.setFont('PretendardMedium', 'normal');
        doc.text(caption, ...transform(doc, left, top + (height - 16) / 2), { baseline: 'top', });
        doc.restoreGraphicsState();
        return roundedRect(doc, {}, left, top, width, height, 0, 0);
      },
      callback,
    ]);

    return next;
  }
}

export function drawSectionBox(doc, data, boxWidth, boxHeight, caption, extraCallback, callbacks) {
  return async (left, top, width, height) => {
    const next = roundedRect(doc, {}, left, top, boxWidth, boxHeight, 0, 0);
    await vbox({
      left,
      top,
      width: boxWidth,
      height: boxHeight,
      gap: 20,
    }, [
      drawSectionCaption(doc, data, caption, extraCallback),
      ...callbacks,
    ]);
    return next;
  };
}

export function drawTableRow(doc, { drawColor, fillColor }, rowHeight, columns) {
  return async (left, top, width, _height) => {
    const height = rowHeight;
    // const next = roundedRect(doc, { lineWidth: 1, drawColor, fillColor }, left, top, width, rowHeight, 0, 0);
    const next = roundedRect(doc, { lineWidth: 1, drawColor, fillColor }, left, top, width, height, 0, 0);
    await hbox({
      left,
      top,
      width,
      height,
    }, columns.map(cell => (
      async (left, top, width, height) => {
        if (cell.text != null) {
          doc.saveGraphicsState();
          doc.setFontSize(pt(doc, 14));
          doc.setFont('PretendardRegular', 'normal');
          const text = cell.text.toString();
          const textWidth = doc.getTextWidth(text) / ratio(doc);
          const color = cell.color || '#667085';
          doc.setTextColor(color);
          await cell.callback && cell.callback(cell, {
            left,
            top,
            width,
            height,
            textWidth,
          });
          if ((text || '').length) {
            switch (cell.align) {
              case 'left':
                doc.text(text, ...transform(doc, left + (cell.paddingLeft || 0), top + (height - 14) / 2), { align: 'left', baseline: 'top', });
                break;
              case 'right':
                doc.text(text, ...transform(doc, left + cell.width - (cell.paddingRight || 0), top + (height - 14) / 2), { align: 'right', baseline: 'top', });
                break;
              default:
                doc.text(text, ...transform(doc, left + cell.width / 2, top + (height - 14) / 2), { align: 'center', baseline: 'top', });
                break;
            }
          }
          doc.restoreGraphicsState();
        }

        // return roundedRect(doc, { lineWidth: 1, drawColor: '#D8D8D8' }, left, top, cell.width, rowHeight, 0, 0);
        return roundedRect(doc, {}, left, top, cell.width, height, 0, 0);
      }
    )));
    return next;
  };
}

export async function drawSummaryValue(doc, { left, top, width, _height }, caption, callbacks) {
  const height = 50;
  await vbox({
    paddingLeft: 10,
    paddingRight: 10,
    left,
    top,
    width,
    height,
    gap: 5,
  }, [
    async (left, top, width, height) => {
      doc.saveGraphicsState();
      doc.setFontSize(pt(doc, 13));
      doc.setTextColor('#666666');
      doc.setFont('PretendardSemiBold', 'normal');
      doc.text(caption, ...transform(doc, left, top + (14 - 13) / 2), { align: 'left', baseline: 'top' });
      doc.restoreGraphicsState();
      return roundedRect(doc, {}, left, top, width, 14, 0, 0);
    },
    async (left, top, width, _height) => {
      const height = 34
      await hbox({
        left,
        top,
        width,
        height,
      }, callbacks);
      return roundedRect(doc, {}, left, top, width, height, 0, 0);
    },
  ]);
  return roundedRect(doc, {}, left, top, width, height, 0, 0);
}

export async function drawPage1(doc, title, page, data, dataType) {
  const {
    glocose_data,
  } = data || {};
  const {
    bld_sugr_analyze_day_list = [],
    bld_sugr_analyze_time_zone_list = [],
    bld_sugr_analyze_week_days_weekend_list = [],
  } = glocose_data || {};

  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    async (left, top, width, height) => {
      const boxTop = top;
      await hbox({
        left,
        top,
        width,
        height,
        gap: 10,
      }, [
        drawSummaryBox(doc, data, 515, 124, T('혈당통계'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('평균혈당'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    const color_type = CHECK_COLORS('B', data.glocose_data.avg_bld_sugr || 0);
                    const color = color_type === 'good' ? '#1D82FF' : '#FF7D5E';
                    doc.setTextColor(color);
                    doc.setFont('PretendardBold', 'normal');
                    const text = (data.glocose_data.avg_bld_sugr || '').toString();
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('mg/dL'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + 108, top, left + 108, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, 118, 50, 0, 0);
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('GMI'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    const color_type = CHECK_COLORS('GMI', data.glocose_data.gmi || 0);
                    const color = color_type === 'good' ? '#1D82FF' : '#FF7D5E';
                    doc.setTextColor(color);
                    doc.setFont('PretendardBold', 'normal');
                    const text = (data.glocose_data.gmi || '').toString();
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('%'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + 108, top, left + 108, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, 118, 50, 0, 0);
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('TIR'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    const color_type = CHECK_COLORS('TIR', data.glocose_data.tir || 0);
                    const color = color_type === 'good' ? '#1D82FF' : '#FF7D5E';
                    doc.setTextColor(color);
                    doc.setFont('PretendardBold', 'normal');
                    const text = (data.glocose_data.tir || '').toString();
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('%'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + 108, top, left + 108, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, 118, 50, 0, 0);
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('CV'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    const color_type = CHECK_COLORS('CV', data.glocose_data.cv || 0);
                    const color = color_type === 'good' ? '#1D82FF' : '#FF7D5E';
                    doc.setTextColor(color);
                    doc.setFont('PretendardBold', 'normal');
                    const text = (data.glocose_data.cv || '').toString();
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('%'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);
                return roundedRect(doc, {}, left, top, 118, 50, 0, 0);
              },
              async (_left, _top, _width, _height) => {
                // 좋음 주의 legend
                const left = _width - 10;
                const top = boxTop + 20;
                const width = 68;
                const height = 13;
                await hbox({
                  left,
                  top,
                  width,
                  height,
                }, [
                  async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#0070FF' }, left, top + (13 - 6) / 2, 6, 6, 3, 3),
                  async (left, top, width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 11));
                    doc.setTextColor('#667085');
                    doc.setFont('PretendardBold', 'normal');
                    doc.text(T('좋음'), ...transform(doc, left + 3, top + (13 - 11) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, 28, 13, 0, 0);
                  },
                  async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#FF7D5E' }, left, top + (13 - 6) / 2, 6, 6, 3, 3),
                  async (left, top, width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 11));
                    doc.setTextColor('#667085');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('주의'), ...transform(doc, left + 3, top + (13 - 11) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, 28, 13, 0, 0);
                  },
                ])
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 292, 124, T('이상혈당 발생'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
              gap: 2,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('전체'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('#333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = ((data.glocose_data.high_bld_sugr_cnt || 0) + (data.glocose_data.low_bld_sugr_cnt || 0)).toString();
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('건'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, 120, 50, 0, 0);
              },
              async (left, top, width, height) => {
                await hbox({
                  left,
                  top,
                  width,
                  height,
                  gap: 2,
                }, [
                  async (left, top, _width, _height) => {
                    const width = 64;
                    const height = 50;
                    const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);

                    await vbox({
                      paddingLeft: 10,
                      paddingRight: 10,
                      paddingTop: 5,
                      paddingBottom: 5,
                      left,
                      top,
                      width,
                      height,
                      gap: 10,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 14;
                        await hbox({
                          left,
                          top,
                          width,
                          height,
                        }, [
                          async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#FF995A' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 13));
                            doc.setTextColor('#666666');
                            doc.setFont('PretendardSemiBold', 'normal');
                            doc.text(T('고혈당'), ...transform(doc, left + 3, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                            doc.restoreGraphicsState();
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ])
                        return roundedRect(doc, {}, left, top, width, 14, 0, 0);
                      },
                      async (left, top, width, _height) => {
                        const height = 17;
                        await hbox({
                          left: left + 8,
                          top,
                          width,
                          height,
                        }, [
                          async (left, top, _width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 14));
                            doc.setTextColor('#333333');
                            doc.setFont('PretendardBold', 'normal');
                            const text = (data.glocose_data.high_bld_sugr_cnt || 0).toString();
                            const width = doc.getTextWidth(text) / ratio(doc);
                            doc.text(text, ...transform(doc, left, top + (height - 14) / 2), { align: 'left', baseline: 'top' });
                            doc.restoreGraphicsState();
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                          async (left, top, _width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 12));
                            doc.setTextColor('#999999');
                            doc.setFont('PretendardRegular', 'normal');
                            doc.text(T('건'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                            doc.restoreGraphicsState();
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ])
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);

                    return rect;
                  },
                  async (left, top, _width, _height) => {
                    const width = 64;
                    const height = 50;
                    const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);

                    await vbox({
                      paddingLeft: 10,
                      paddingRight: 10,
                      paddingTop: 5,
                      paddingBottom: 5,
                      left,
                      top,
                      width,
                      height,
                      gap: 10,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 14;
                        await hbox({
                          left,
                          top,
                          width,
                          height,
                        }, [
                          async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#756CE4' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 13));
                            doc.setTextColor('#666666');
                            doc.setFont('PretendardSemiBold', 'normal');
                            doc.text(T('저혈당'), ...transform(doc, left + 3, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                            doc.restoreGraphicsState();
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ])
                        return roundedRect(doc, {}, left, top, width, 14, 0, 0);
                      },
                      async (left, top, width, _height) => {
                        const height = 17;
                        await hbox({
                          left: left + 8,
                          top,
                          width,
                          height,
                        }, [
                          async (left, top, _width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 14));
                            doc.setTextColor('#333333');
                            doc.setFont('PretendardBold', 'normal');
                            const text = (data.glocose_data.low_bld_sugr_cnt || 0).toString();
                            const width = doc.getTextWidth(text) / ratio(doc);
                            doc.text(text, ...transform(doc, left, top + (height - 14) / 2), { align: 'left', baseline: 'top' });
                            doc.restoreGraphicsState();
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                          async (left, top, _width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 12));
                            doc.setTextColor('#999999');
                            doc.setFont('PretendardRegular', 'normal');
                            doc.text(T('건'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                            doc.restoreGraphicsState();
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ])
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);

                    return rect;
                  },
                ]);
                return roundedRect(doc, {}, left, top, 130, 50, 0, 0);
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 293, 124, T('혈당패턴'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('주중'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('#333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = (data.glocose_data.week_days_bld_sugr_mbti || '').toString();
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + 116, top, left + 116, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, 126, 50, 0, 0);
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('주말'), [
                  async (left, top, _width, height) => {
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('#333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = (data.glocose_data.weekend_bld_sugr_mbti || '').toString();
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, 126, 50, 0, 0);
              },
            ]);
            return roundedRect(doc, {}, left, top, 253, 50, 0, 0);
          },
        ]),
      ]);
      return roundedRect(doc, {}, left, top, 1120, 124, 0, 0);
    },
    drawSectionBox(doc, data, 1120, 928, T('혈당개요 (AGP)'), null, [
      async (left, top, width, height) => {
        const position = transform(doc, left, top, 1120, 300);
        await addImage(doc, 'glocose_agp', data.glocose_profile_chart.url, ...position);

        return roundedRect(doc, {}, left, top, 1120, 300, 0, 0);
      },
      async (left, top, width, height) => {
        const ratio = 1120 / data.glocose_tir_chart.width;
        const position = transform(doc, left, top, data.glocose_tir_chart.width * ratio, data.glocose_tir_chart.height * ratio);
        if (data.glocose_tir_chart.url) {
          await addImage(doc, 'glocose_tir_chart', data.glocose_tir_chart.url, ...position);
        }
        return roundedRect(doc, {}, left, top, data.glocose_tir_chart.width * ratio, data.glocose_tir_chart.height * ratio, 0, 0);
      },
      async (left, top, width, _height) => {
        const height = 296;

        const cellWidth = (1120 - 95) / 14;
        const columnWidths = [
          95,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
        ];

        const headers = [T('구분'), ...GLOCOSE_COLUMNS].map((text, index) => ({
          text,
          width: columnWidths[index],
        }));

        const sums = {
          tars: 0,
          tirs: 0,
          tbrs: 0,
          gmis: 0,
          cvs: 0,
          averages: 0,
          highs: 0,
          lows: 0,
        };

        bld_sugr_analyze_day_list.forEach(item => {
          const {
            day_tp_cd,
            avg_bld_sugr,
            high_bld_sugr,
            low_bld_sugr,
            gmi,
            cv,
            tir,
            tar,
            tbr,
          } = item;
          sums.tars += tar;
          sums.tirs += tir;
          sums.tbrs += tbr;
          sums.cvs += cv;
          sums.gmis += gmi;
          sums.averages += avg_bld_sugr;
          sums.highs += high_bld_sugr;
          sums.lows += low_bld_sugr;
        });

        const days = bld_sugr_analyze_day_list.reduce((a, c) => {
          a[c.day_tp_cd - 1] = c;
          return a;
        }, Array.from({ length: WEEK_DAY_COLUMNS.length }));

        const paddingRight = 25;
        const tars = [
          { align: 'center', paddingRight, text: T('TAR'), width: columnWidths[0] },
          { callback: color_callback(doc, 'TAR'), align: 'right', paddingRight, text: +(sums.tars / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'TAR'), align: 'right', paddingRight, text: (days[index] || {}).tar, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'TAR'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).tar, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'TAR'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).tar, width: columnWidths[index + 11] }))
        ];
        const tirs = [
          { align: 'center', paddingRight, text: T('TIR'), width: columnWidths[0] },
          { callback: color_callback(doc, 'TIR'), align: 'right', paddingRight, text: +(sums.tirs / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'TIR'), align: 'right', paddingRight, text: (days[index] || {}).tir, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'TIR'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).tir, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'TIR'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).tir, width: columnWidths[index + 11] }))
        ];
        const tbrs = [
          { align: 'center', paddingRight, text: T('TBR'), width: columnWidths[0] },
          { callback: color_callback(doc, 'TBR'), align: 'right', paddingRight, text: +(sums.tbrs / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'TBR'), align: 'right', paddingRight, text: (days[index] || {}).tbr, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'TBR'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).tbr, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'TBR'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).tbr, width: columnWidths[index + 11] }))
        ];
        const gmis = [
          { align: 'center', paddingRight, text: T('GMI'), width: columnWidths[0] },
          { callback: color_callback(doc, 'GMI'), align: 'right', paddingRight, text: +(sums.gmis / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'GMI'), align: 'right', paddingRight, text: (days[index] || {}).gmi, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'GMI'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).gmi, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'GMI'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).gmi, width: columnWidths[index + 11] }))
        ];
        const cvs = [
          { align: 'center', paddingRight, text: T('CV'), width: columnWidths[0] },
          { callback: color_callback(doc, 'CV'), align: 'right', paddingRight, text: +(sums.cvs / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'CV'), align: 'right', paddingRight, text: (days[index] || {}).cv, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'CV'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).cv, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'CV'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).cv, width: columnWidths[index + 11] }))
        ];
        const averages = [
          { align: 'center', paddingRight, text: T('평균혈당'), width: columnWidths[0] },
          { callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: +(sums.averages / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_day_list[index] || {}).avg_bld_sugr, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).avg_bld_sugr, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).avg_bld_sugr, width: columnWidths[index + 11] }))
        ];
        const highs = [
          { align: 'center', paddingRight, text: T('최고혈당'), width: columnWidths[0] },
          { callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: +(sums.highs / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_day_list[index] || {}).high_bld_sugr, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).high_bld_sugr, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).high_bld_sugr, width: columnWidths[index + 11] }))
        ];
        const lows = [
          { align: 'center', paddingRight, text: T('최저혈당'), width: columnWidths[0] },
          { callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: +(sums.lows / (bld_sugr_analyze_day_list.length || 1)).toFixed(2), width: columnWidths[1] },
          ...WEEK_DAY_COLUMNS.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_day_list[index] || {}).low_bld_sugr, width: columnWidths[index + 2] })),
          ...WEEK_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_week_days_weekend_list[index] || {}).low_bld_sugr, width: columnWidths[index + 9] })),
          ...DAY_ZONE_TYPES.map((_, index) => ({ callback: color_callback(doc, 'B'), align: 'right', paddingRight, text: (bld_sugr_analyze_time_zone_list[index] || {}).low_bld_sugr, width: columnWidths[index + 11] }))
        ];

        await vbox({
          left,
          top,
          width,
          height,
          gap: 5,
        }, [
          drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
          drawTableRow(doc, {}, 27, tars),
          drawTableRow(doc, {}, 27, tirs),
          drawTableRow(doc, {}, 27, tbrs),
          drawTableRow(doc, {}, 27, gmis),
          drawTableRow(doc, {}, 27, cvs),
          drawTableRow(doc, {}, 27, averages),
          drawTableRow(doc, {}, 27, highs),
          drawTableRow(doc, {}, 27, lows),
        ]);

        const columnLines = [
          columnWidths.slice(0, 2).reduce((a, c) => a + c, 0),
          columnWidths.slice(0, 9).reduce((a, c) => a + c, 0),
          columnWidths.slice(0, 11).reduce((a, c) => a + c, 0),
        ];
        columnLines.forEach(line => {
          doc.saveGraphicsState();
          doc.setLineWidth(transform(doc, 1));
          doc.setDrawColor('#D8D8D8');
          doc.setLineDashPattern(transform(doc, 2, 2), 0);
          doc.line(...transform(doc, left + line, top, left + line, top + 296), 'S');
          doc.restoreGraphicsState();
        });

        // 각 row의 높이에 gap 5를 더한 값
        const rowHeights = [
          38 + 5,
          27 + 5,
          27 + 5,
          27 + 5,
          27 + 5,
          27 + 5,
          27 + 5,
          27 + 5,
          27,
        ];

        const rowLines = [
          0,
          rowHeights.slice(0, 4).reduce((a, c) => a + c, 0),
          rowHeights.slice(0, 6).reduce((a, c) => a + c, 0),
          296,
        ];

        rowLines.forEach(line => {
          doc.saveGraphicsState();
          doc.setLineWidth(transform(doc, line === 0 ? 2 : 1));
          doc.setDrawColor('#2E5DCC');
          // gap의 반만큼 빼기
          doc.line(...transform(doc, left, top + line - 2.5, left + 1120, top + line - 2.5), 'S');
          doc.restoreGraphicsState();
        });

        return roundedRect(doc, {}, left, top, 1120, 296, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage2(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    async (left, top, width, height) => {
      await vbox({
        left,
        top,
        width,
        height,
        gap: 20,
      }, [
        drawSectionBox(doc, data, 1120, 262, T('이상혈당 분포'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
              gap: 20
            }, [
              async (left, top, width, height) => {
                const position = transform(doc, left, top, 400, 200);
                if (data.glocose_distrubution_bar_chart.url) {
                  await addImage(doc, 'glocose_distrubution_bar_chart', data.glocose_distrubution_bar_chart.url, ...position);
                }
                return roundedRect(doc, {}, left, top, 400, 200, 0, 0)
              },
              async (left, top, width, height) => {
                const position = transform(doc, left, top, 700, 200);
                if (data.glocose_distrubution_scatter_chart.url) {
                  await addImage(doc, 'glocose_distrubution_scatter_chart', data.glocose_distrubution_scatter_chart.url, ...position);
                }
                return roundedRect(doc, {}, left, top, 700, 200, 0, 0)
              },
            ])
            return roundedRect(doc, {}, left, top, 1120, 210, 0, 0);
          },
        ]),
        drawSectionBox(doc, data, 1120, 262, T('고혈당 발생'), null, [
          async (left, top, width, height) => {
            const cellWidth = (1120) / 8;
            const columnWidths = [
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth
            ];
            const headers = [
              T('No.'),
              T('날짜'),
              T('발생시간대'),
              T('지속시간(분)'),
              T('최고혈당'),
              T('식사'),
              T('2시간내 활동(보)'),
              T('2시간내 볼루스(U)'),
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            const { list = [] } = data.glocose_high_list;
            await vbox({
              left,
              top,
              width,
              height,
              gap: 5,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              ...list.map((item, index) => {
                const {
                  occur_start_time,
                  occur_end_time,
                  occur_time_name,
                  duration,
                  highest_bld_sugr,
                  '2_hr_within_insulin_amt': insulin_amt,
                  '2_hr_within_main_food': main_food,
                  '2_hr_within_step_cnt': step_cnt,
                } = item;
                const start_time = !occur_start_time ? '' : fromFormat(occur_start_time, 'yyyyMMddHHmmss').toFormat('yy.MM.dd HH:mm');
                const end_time = !occur_end_time ? '' : fromFormat(occur_end_time, 'yyyyMMddHHmmss').toFormat('HH:mm');
                const columns = [
                  index + 1,
                  T('{0} ~ {1}', start_time || '', end_time || ''),
                  occur_time_name || '',
                  numberWithComma(duration || 0),
                  highest_bld_sugr || 0,
                  main_food || '',
                  numberWithComma(step_cnt || 0),
                  insulin_amt || 0
                ].map((column, index) => {
                  const callback = index === 4 ? color_callback(doc, 'B') : null;
                  return {
                    callback,
                    align: 'center',
                    text: column,
                    width: columnWidths[index],
                  };
                });
                return drawTableRow(doc, {}, 27, columns);
              }),
            ]);

            return roundedRect(doc, {}, left, top, 1120, 200, 0, 0);
          },
        ]),
        drawSectionBox(doc, data, 1120, 262, T('저혈당 발생'), null, [
          async (left, top, width, height) => {
            const cellWidth = (1120) / 8;
            const columnWidths = [
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth
            ];
            const headers = [
              T('No.'),
              T('날짜'),
              T('발생시간대'),
              T('지속시간(분)'),
              T('최고혈당'),
              T('식사'),
              T('2시간내 활동(보)'),
              T('2시간내 볼루스(U)'),
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            const { list = [] } = data.glocose_low_list;
            await vbox({
              left,
              top,
              width,
              height,
              gap: 5,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              ...list.map((item, index) => {
                const {
                  occur_start_time,
                  occur_end_time,
                  occur_time_name,
                  duration,
                  highest_bld_sugr,
                  '2_hr_within_insulin_amt': insulin_amt,
                  '2_hr_within_main_food': main_food,
                  '2_hr_within_step_cnt': step_cnt,
                } = item;
                const start_time = !occur_start_time ? '' : fromFormat(occur_start_time, 'yyyyMMddHHmmss').toFormat('yy.MM.dd HH:mm');
                const end_time = !occur_end_time ? '' : fromFormat(occur_end_time, 'yyyyMMddHHmmss').toFormat('HH:mm');
                const columns = [
                  index + 1,
                  T('{0} ~ {1}', start_time || '', end_time || ''),
                  occur_time_name || '',
                  numberWithComma(duration || 0),
                  highest_bld_sugr || 0,
                  main_food || '',
                  numberWithComma(step_cnt || 0),
                  insulin_amt || 0
                ].map((column, index) => {
                  const callback = index === 4 ? color_callback(doc, 'B') : null;
                  return {
                    callback,
                    align: 'center',
                    text: column,
                    width: columnWidths[index],
                  };
                });
                return drawTableRow(doc, {}, 27, columns);
              }),
            ]);

            return roundedRect(doc, {}, left, top, 1120, 200, 0, 0);
          },
        ]),
      ])

      return roundedRect(doc, {}, left, top, 1120, 850, 0, 0);
    },
    drawSectionBox(doc, data, 1120, 257, T('혈당패턴'), null, [
      async (left, top, width_, height_) => {
        const width = 1120;
        const height = 205;
        await hbox({
          left,
          top,
          width,
          height,
          gap: 20,
        }, [
          async (left, top, width_, height) => {
            const width = 550;
            await vbox({
              left,
              top,
              width,
              height,
              gap: 10,
            }, [
              async (left, top, width_, height_) => {
                const height = 147;
                await hbox({
                  left,
                  top,
                  width,
                  height,
                }, [
                  async (left, top, width, height) => {
                    await vbox({
                      left,
                      top,
                      width,
                      height,
                      gap: 5,
                    }, [
                      async (left, top, width, height) => roundedRect(doc, {}, left, top, width, 5, 0, 0), // margin
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setGState(new doc.GState({ 'opacity': 0.2 }));
                        roundedRect(doc, { lineWidth: 1, drawColor: '#347AFF', fillColor: '#347AFF' }, left, top, 100, 34, 8, 8);
                        doc.restoreGraphicsState();

                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 14));
                        doc.setTextColor('#347AFF');
                        doc.setFont('PretendardSemiBold', 'normal');
                        doc.text(T('주중 대표패턴'), ...transform(doc, left + 100 / 2, top + 34 / 2), { align: 'center', baseline: 'middle' });
                        doc.restoreGraphicsState();

                        return roundedRect(doc, {}, left, top, width, 34, 0, 0);
                      },
                      async (left, top, width, height) => {
                        // L-H-
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 32));
                        doc.setTextColor('#000000');
                        doc.setFont('PretendardBold', 'normal');
                        doc.text(data.glocose_data.week_days_bld_sugr_mbti || '', ...transform(doc, left, top + 40 / 2), { align: 'left', baseline: 'middle' });
                        doc.restoreGraphicsState();

                        return roundedRect(doc, {}, left, top, width, 40, 0, 0);
                      },
                      async (left, top, width, height) => {
                        // 새벽 저혈당형, 오후 고혈당형

                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 14));
                        doc.setTextColor('#667085');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(data.glocose_data.week_days_bld_sugr_mbti_content || '', ...transform(doc, left, top + 40 / 2), { align: 'left', baseline: 'middle' });
                        doc.restoreGraphicsState();

                        return roundedRect(doc, {}, left, top, width, 17, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, 320, 120, 0, 0);
                  },
                  async (left, top, width, height) => {
                    const ratio = 220 / data.glocose_pattern_weekday_chart.width;
                    const position = transform(doc, left, top, data.glocose_pattern_weekday_chart.width * ratio, data.glocose_pattern_weekday_chart.height * ratio);
                    if (data.glocose_pattern_weekday_chart.url) {
                      await addImage(doc, 'glocose_pattern_weekday_chart', data.glocose_pattern_weekday_chart.url, ...position);
                    }
                    return roundedRect(doc, {}, left, top, 220, 147, 0, 0);
                  },
                ])
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
              async (left, top, width, height) => {
                // table

                const cellWidth = (530) / 4;
                const columnWidths = [
                  cellWidth,
                  cellWidth,
                  cellWidth,
                  cellWidth
                ];
                const headers = [
                  T('새벽(0-6H)'),
                  T('오전(6-12H)'),
                  T('오후(12-18H)'),
                  T('저녁(18-24H)'),
                ].map((text, index) => ({
                  text,
                  width: columnWidths[index],
                }));

                const columns = [
                  data.glocose_data.week_days_dawn_pattern_info,
                  data.glocose_data.week_days_morning_pattern_info,
                  data.glocose_data.week_days_lunch_pattern_info,
                  data.glocose_data.week_days_evening_pattern_info,
                ].map((text, index) => ({
                  align: 'center',
                  text,
                  width: columnWidths[index],
                }));

                await vbox({
                  left,
                  top,
                  width,
                  height: 55,
                }, [
                  drawTableRow(doc, { fillColor: '#E8ECF2' }, 28, headers),
                  drawTableRow(doc, {}, 27, columns),
                ]);

                return roundedRect(doc, {}, left, top, width, 55, 0, 0);
              },
            ])
            return roundedRect(doc, {}, left, top, width, height, 0, 0);
          },
          async (left, top, width_, height) => {
            const width = 550;
            await vbox({
              left,
              top,
              width,
              height,
              gap: 10,
            }, [
              async (left, top, width_, height_) => {
                const height = 147;
                await hbox({
                  left,
                  top,
                  width,
                  height,
                }, [
                  async (left, top, width, height) => {
                    await vbox({
                      left,
                      top,
                      width,
                      height,
                      gap: 5,
                    }, [
                      async (left, top, width, height) => roundedRect(doc, {}, left, top, width, 5, 0, 0), // margin
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setGState(new doc.GState({ 'opacity': 0.2 }));
                        roundedRect(doc, { lineWidth: 1, drawColor: '#347AFF', fillColor: '#347AFF' }, left, top, 100, 34, 8, 8);
                        doc.restoreGraphicsState();

                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 14));
                        doc.setTextColor('#347AFF');
                        doc.setFont('PretendardSemiBold', 'normal');
                        doc.text(T('주말 대표패턴'), ...transform(doc, left + 100 / 2, top + 34 / 2), { align: 'center', baseline: 'middle' });
                        doc.restoreGraphicsState();

                        return roundedRect(doc, {}, left, top, width, 34, 0, 0);
                      },
                      async (left, top, width, height) => {
                        // V--H
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 32));
                        doc.setTextColor('#000000');
                        doc.setFont('PretendardBold', 'normal');
                        doc.text(data.glocose_data.weekend_bld_sugr_mbti || '', ...transform(doc, left, top + 40 / 2), { align: 'left', baseline: 'middle' });
                        doc.restoreGraphicsState();

                        return roundedRect(doc, {}, left, top, width, 40, 0, 0);
                      },
                      async (left, top, width, height) => {
                        // 새벽변동형(고혈당+저혈당), 저녁 고혈당형
                        doc.setFontSize(pt(doc, 14));
                        doc.setTextColor('#667085');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(data.glocose_data.weekend_bld_sugr_mbti_content || '', ...transform(doc, left, top + 40 / 2), { align: 'left', baseline: 'middle' });

                        return roundedRect(doc, {}, left, top, width, 17, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, 320, 120, 0, 0);
                  },
                  async (left, top, width, height) => {
                    const ratio = 220 / data.glocose_pattern_weekend_chart.width;
                    const position = transform(doc, left, top, data.glocose_pattern_weekend_chart.width * ratio, data.glocose_pattern_weekend_chart.height * ratio);
                    if (data.glocose_pattern_weekend_chart.url) {
                      await addImage(doc, 'glocose_pattern_weekend_chart', data.glocose_pattern_weekend_chart.url, ...position);
                    }
                    return roundedRect(doc, {}, left, top, 220, 147, 0, 0);
                  },
                ])
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
              async (left, top, width, height) => {
                // table

                const cellWidth = (530) / 4;
                const columnWidths = [
                  cellWidth,
                  cellWidth,
                  cellWidth,
                  cellWidth
                ];
                const headers = [
                  T('새벽(0-6H)'),
                  T('오전(6-12H)'),
                  T('오후(12-18H)'),
                  T('저녁(18-24H)'),
                ].map((text, index) => ({
                  text,
                  width: columnWidths[index],
                }));

                const columns = [
                  data.glocose_data.weekend_dawn_pattern_info,
                  data.glocose_data.weekend_morning_pattern_info,
                  data.glocose_data.weekend_lunch_pattern_info,
                  data.glocose_data.weekend_evening_pattern_info,
                ].map((text, index) => ({
                  align: 'center',
                  text,
                  width: columnWidths[index],
                }));

                await vbox({
                  left,
                  top,
                  width,
                  height: 55,
                }, [
                  drawTableRow(doc, { fillColor: '#E8ECF2' }, 28, headers),
                  drawTableRow(doc, {}, 27, columns),
                ]);

                return roundedRect(doc, {}, left, top, width, 55, 0, 0);
              },
            ])
            return roundedRect(doc, {}, left, top, width, height, 0, 0);
          },
        ])
        return roundedRect(doc, {}, left, top, width, height, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage3(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    async (left, top, width, height) => {
      await vbox({
        left,
        top,
        width,
        height,
        gap: 20,
      }, [
        async (left, top, width, height) => {

          // 식사 레이아웃은 피그마와 실제 구현이 다름
          // 요구사항에 의해서 구조가 변경되었고 그에 따라 수동으로 비교해서 레이아웃 맞춤 402/346/353 => 455/322/322
          // 일별 식사통계내의 식사기록이 일평균 식사기록으로 이동함
          // 20241127 janghaksang
          await hbox({
            left,
            top,
            width,
            height,
            gap: 10,
          }, [
            drawSummaryBox(doc, data, 455, 124, T('일평균 식사'), null, [
              async (left, top, width, height) => {
                await hbox({
                  left,
                  top,
                  width,
                  height,
                }, [
                  async (left, top, width, height) => {
                    await drawSummaryValue(doc, { left, top, width, height }, T('일평균 식사기록'), [
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 28));
                        doc.setTextColor('##333333');
                        doc.setFont('PretendardBold', 'normal');
                        const text = numberWithComma(data.diet_data.avg_meal_cnt || 0);
                        const width = doc.getTextWidth(text) / ratio(doc);
                        doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('회'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);

                    doc.saveGraphicsState();
                    doc.setLineWidth(transform(doc, 1));
                    doc.setDrawColor('#D8D8D8');
                    doc.setLineDashPattern(transform(doc, 2, 2), 0);
                    doc.line(...transform(doc, left + width / 20 * 5 - 10, top, left + width / 20 * 5 - 10, top + 50), 'S');
                    doc.restoreGraphicsState();

                    return roundedRect(doc, {}, left, top, width / 20 * 5, 50, 0, 0)
                  },
                  async (left, top, width, height) => {
                    await drawSummaryValue(doc, { left, top, width, height }, T('섭취칼로리'), [
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 28));
                        doc.setTextColor('##333333');
                        doc.setFont('PretendardBold', 'normal');
                        const text = numberWithComma(data.diet_data.avg_intaken_cal || 0);
                        const width = doc.getTextWidth(text) / ratio(doc);
                        doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('kcal'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);

                    return roundedRect(doc, {}, left, top, width / 20 * 6, 50, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    const width = _width / 20 * 9;
                    await hbox({
                      left,
                      top,
                      width,
                      height,
                      gap: 2,
                    }, [
                      async (left, top, _width, _height) => {
                        const width = _width / 20 * 11 - 5;
                        const height = 50;
                        const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);
                        await vbox({
                          paddingLeft: 10,
                          paddingRight: 10,
                          paddingTop: 5,
                          paddingBottom: 5,
                          left,
                          top,
                          width,
                          height,
                          gap: 10,
                        }, [
                          async (left, top, width, _height) => {
                            const height = 14;
                            await hbox({
                              left,
                              top,
                              width,
                              height,
                            }, [
                              async (left, top, width, height) => {
                                doc.saveGraphicsState();
                                doc.setFontSize(pt(doc, 13));
                                doc.setTextColor('#666666');
                                doc.setFont('PretendardSemiBold', 'normal');
                                doc.text(T('최대'), ...transform(doc, left + 3, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                                doc.restoreGraphicsState();
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                            ])
                            return roundedRect(doc, {}, left, top, width, 14, 0, 0);
                          },
                          async (left, top, width, _height) => {
                            const height = 17;
                            await hbox({
                              left: left + 8,
                              top,
                              width,
                              height,
                            }, [
                              async (left, top, _width, height) => {
                                doc.saveGraphicsState();
                                doc.setFontSize(pt(doc, 14));
                                doc.setTextColor('#333333');
                                doc.setFont('PretendardBold', 'normal');
                                const text = numberWithComma(data.diet_data.max_intaken_cal || 0);
                                const width = doc.getTextWidth(text) / ratio(doc);
                                doc.text(text, ...transform(doc, left, top + (height - 14) / 2), { align: 'left', baseline: 'top' });
                                doc.restoreGraphicsState();
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                              async (left, top, _width, height) => {
                                doc.saveGraphicsState();
                                doc.setFontSize(pt(doc, 12));
                                doc.setTextColor('#999999');
                                doc.setFont('PretendardRegular', 'normal');
                                doc.text(T('kcal'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                                doc.restoreGraphicsState();
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                            ])
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return rect;
                      },
                      async (left, top, _width, _height) => {
                        const width = _width / 20 * 9 - 5;
                        const height = 50;
                        const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);

                        await vbox({
                          paddingLeft: 10,
                          paddingRight: 10,
                          paddingTop: 5,
                          paddingBottom: 5,
                          left,
                          top,
                          width,
                          height,
                          gap: 10,
                        }, [
                          async (left, top, width, _height) => {
                            const height = 14;
                            await hbox({
                              left,
                              top,
                              width,
                              height,
                            }, [
                              async (left, top, width, height) => {
                                doc.saveGraphicsState();
                                doc.setFontSize(pt(doc, 13));
                                doc.setTextColor('#666666');
                                doc.setFont('PretendardSemiBold', 'normal');
                                doc.text(T('최소'), ...transform(doc, left + 3, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                                doc.restoreGraphicsState();
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                            ])
                            return roundedRect(doc, {}, left, top, width, 14, 0, 0);
                          },
                          async (left, top, width, _height) => {
                            const height = 17;
                            await hbox({
                              left: left + 8,
                              top,
                              width,
                              height,
                            }, [
                              async (left, top, _width, height) => {
                                doc.saveGraphicsState();
                                doc.setFontSize(pt(doc, 14));
                                doc.setTextColor('#333333');
                                doc.setFont('PretendardBold', 'normal');
                                const text = numberWithComma(data.diet_data.min_intaken_cal || 0);
                                const width = doc.getTextWidth(text) / ratio(doc);
                                doc.text(text, ...transform(doc, left, top + (height - 14) / 2), { align: 'left', baseline: 'top' });
                                doc.restoreGraphicsState();
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                              async (left, top, _width, height) => {
                                doc.saveGraphicsState();
                                doc.setFontSize(pt(doc, 12));
                                doc.setTextColor('#999999');
                                doc.setFont('PretendardRegular', 'normal');
                                doc.text(T('kcal'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                                doc.restoreGraphicsState();
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                            ])
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);

                        return rect;
                      },
                    ]);

                    return roundedRect(doc, {}, left, top, width, 50, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width, 50, 0, 0)
              },
            ]),
            drawSummaryBox(doc, data, 322, 124, T('일평균 탄단지'), null, [
              async (left, top, width, height) => {
                await hbox({
                  left,
                  top,
                  width,
                  height,
                }, [
                  async (left, top, width, height) => {
                    await drawSummaryValue(doc, { left, top, width, height }, T('탄수화물'), [
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 28));
                        doc.setTextColor('##333333');
                        doc.setFont('PretendardBold', 'normal');
                        const text = numberWithComma(data.diet_data.carbo_intaken_cal_rate || 0);
                        const width = doc.getTextWidth(text) / ratio(doc);
                        doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('g'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);

                    doc.saveGraphicsState();
                    doc.setLineWidth(transform(doc, 1));
                    doc.setDrawColor('#D8D8D8');
                    doc.setLineDashPattern(transform(doc, 2, 2), 0);
                    doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                    doc.restoreGraphicsState();

                    return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0);
                  },
                  async (left, top, width, height) => {
                    await drawSummaryValue(doc, { left, top, width, height }, T('단백질'), [
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 28));
                        doc.setTextColor('##333333');
                        doc.setFont('PretendardBold', 'normal');
                        const text = numberWithComma(data.diet_data.protein_intaken_cal_rate || 0);
                        const width = doc.getTextWidth(text) / ratio(doc);
                        doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('g'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);

                    doc.saveGraphicsState();
                    doc.setLineWidth(transform(doc, 1));
                    doc.setDrawColor('#D8D8D8');
                    doc.setLineDashPattern(transform(doc, 2, 2), 0);
                    doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                    doc.restoreGraphicsState();

                    return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0);
                  },
                  async (left, top, width, height) => {
                    await drawSummaryValue(doc, { left, top, width, height }, T('지방'), [
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 28));
                        doc.setTextColor('##333333');
                        doc.setFont('PretendardBold', 'normal');
                        const text = numberWithComma(data.diet_data.fat_intaken_cal_rate || 0);
                        const width = doc.getTextWidth(text) / ratio(doc);
                        doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, _width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('g'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);

                    return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width, 50, 0, 0);
              },
            ]),
            drawSummaryBox(doc, data, 322, 124, T('대표음식'), null, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('이상혈당 발생음식'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 14));
                    doc.setTextColor('#2E5DCC');
                    doc.setFont('PretendardMedium', 'normal');
                    const texts = (data.diet_data.high_bld_sugr_food || '').split(',');
                    await hbox({
                      left,
                      top,
                      width,
                      height: 18,
                      gap: 5,
                    }, texts.map(text => async (left, top, _width, height) => {
                      const width = (doc.getTextWidth(text) + 4) / ratio(doc);
                      doc.text(text, ...transform(doc, left + 6, top + 2), { align: 'left', baseline: 'top' });
                      return roundedRect(doc, { lineWidth: 1, drawColor: '#2E5DCC' }, left, top, width, height, 4, 4);
                    }));
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width, 50, 0, 0);
              },
            ]),
          ]);
          return roundedRect(doc, {}, left, top, 1120, 124, 0, 0);
        },
        drawSummaryLinkBox(doc, data, 1120, 240, T('식사 분포'), null, [
          async (left, top, _width, _height) => {
            const width = 380;
            const height = 200 - 14 - 20;

            await vbox({
              top: top + height - 128,
              left,
              width: 213,
              height: 128,
              gap: 20,
            }, [
              async (left, top, width, _height) => {
                const height = 54;
                await vbox({
                  left,
                  top,
                  width,
                  height,
                }, [
                  async (left, top, width, _height) => {
                    const height = 14;
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 13));
                    doc.setTextColor('#666666');
                    doc.setFont('PretendardSemiBold', 'normal');
                    const text = T('식후 이상혈당 발생');
                    doc.text(text, ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top', });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, width, _height) => {
                    const height = 40;
                    await hbox({
                      left,
                      top,
                      width,
                      height,
                    }, [
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 32));
                        doc.setTextColor('#333333');
                        doc.setFont('PretendardBold', 'normal');
                        const text = (data.diet_data.postprandial_abnormal_rate || 0).toString();
                        const textWidth = doc.getTextWidth(text) / ratio(doc);
                        doc.text(text, ...transform(doc, left, top + height - (height - 32) / 2), { align: 'left', baseline: 'bottom', });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, textWidth, height, 0, 0);
                      },
                      async (_left, top, width, height) => {
                        const left = _left + 5;
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        const text = T('%');
                        const textWidth = doc.getTextWidth(text) / ratio(doc);
                        doc.text(text, ...transform(doc, left, top + height - (height - 32) / 2), { align: 'left', baseline: 'bottom', });
                        doc.restoreGraphicsState();
                        return roundedRect(doc, {}, left, top, textWidth, height, 0, 0);
                      },
                    ])
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
              async (left, top, width, _height) => {
                const height = 54;
                await hbox({
                  left,
                  top,
                  width,
                  height,
                  gap: 5,
                }, [
                  async (left, top, _width, height) => {
                    // 고혈당
                    const width = 64;
                    const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);
                    await vbox({
                      paddingLeft: 10,
                      paddingRight: 10,
                      paddingTop: 5,
                      paddingBottom: 5,
                      left,
                      top,
                      width,
                      height,
                      gap: 3,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 27;
                        await vbox({
                          left,
                          top,
                          width,
                          height,
                          gap: 1,
                        }, [
                          async (left, top, width, _height) => {
                            const height = 14;
                            await hbox({
                              left,
                              top,
                              width,
                              height,
                              gap: 3,
                            }, [
                              async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#FF995A' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                              async (left, top, width, height) => {
                                doc.saveGraphicsState();
                                doc.setFontSize(pt(doc, 13));
                                doc.setTextColor('#666666');
                                doc.setFont('PretendardSemiBold', 'normal');
                                doc.text(T('고혈당'), ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                                doc.saveGraphicsState();
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                            ]);
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, width, _height) => {
                        const height = 17;
                        const gap = 3;

                        const [textWidth, valueWidth] = (() => {
                          doc.saveGraphicsState();
                          doc.setFontSize(pt(doc, 14));
                          doc.setFont('PretendardBold', 'normal');
                          const w1 = doc.getTextWidth((data.diet_data.high_bld_sugr_cnt || 0).toString());
                          doc.setFontSize(pt(doc, 12));
                          doc.setFont('PretendardRegular', 'normal');
                          const w2 = doc.getTextWidth(T('건'));
                          doc.restoreGraphicsState();
                          return [w1 + w2 + gap, w1];
                        })();

                        await hbox({
                          left: left + (width - textWidth) / 2,
                          top,
                          width: textWidth,
                          height,
                          gap,
                        }, [
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 14));
                            doc.setTextColor('#333333');
                            doc.setFont('PretendardBold', 'normal');
                            doc.text((data.diet_data.high_bld_sugr_cnt || 0).toString(), ...transform(doc, left + valueWidth, top + height - (height - 14) / 2), { align: 'right', baseline: 'bottom' });
                            doc.saveGraphicsState();
                            return roundedRect(doc, {}, left, top, valueWidth, height, 0, 0);
                          },
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 12));
                            doc.setTextColor('#999999');
                            doc.setFont('PretendardRegular', 'normal');
                            doc.text(T('건'), ...transform(doc, left, top + height - (height - 12) / 2), { align: 'left', baseline: 'bottom' });
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return rect;
                  },
                  async (left, top, _width, height) => {
                    // 저혈당
                    const width = 64;
                    const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);
                    await vbox({
                      paddingLeft: 10,
                      paddingRight: 10,
                      paddingTop: 5,
                      paddingBottom: 5,
                      left,
                      top,
                      width,
                      height,
                      gap: 3,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 27;
                        await vbox({
                          left,
                          top,
                          width,
                          height,
                          gap: 1,
                        }, [
                          async (left, top, width, _height) => {
                            const height = 14;
                            await hbox({
                              left,
                              top,
                              width,
                              height,
                              gap: 3,
                            }, [
                              async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#756CE4' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                              async (left, top, width, height) => {
                                doc.setFontSize(pt(doc, 13));
                                doc.setTextColor('#666666');
                                doc.setFont('PretendardSemiBold', 'normal');
                                doc.text(T('저혈당'), ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                            ]);
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, width, _height) => {
                        const height = 17;
                        const gap = 3;

                        const [textWidth, valueWidth] = (() => {
                          doc.saveGraphicsState();
                          doc.setFontSize(pt(doc, 14));
                          doc.setFont('PretendardBold', 'normal');
                          const w1 = doc.getTextWidth((data.diet_data.low_bld_sugr_cnt || 0).toString());
                          doc.setFontSize(pt(doc, 12));
                          doc.setFont('PretendardRegular', 'normal');
                          const w2 = doc.getTextWidth(T('건'));
                          doc.restoreGraphicsState();
                          return [w1 + w2 + gap, w1];
                        })();

                        await hbox({
                          left: left + (width - textWidth) / 2,
                          top,
                          width: textWidth,
                          height,
                          gap,
                        }, [
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 14));
                            doc.setTextColor('#333333');
                            doc.setFont('PretendardBold', 'normal');
                            doc.text((data.diet_data.low_bld_sugr_cnt || 0).toString(), ...transform(doc, left + valueWidth, top + height - (height - 14) / 2), { align: 'right', baseline: 'bottom' });
                            doc.saveGraphicsState();
                            return roundedRect(doc, {}, left, top, valueWidth, height, 0, 0);
                          },
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 12));
                            doc.setTextColor('#999999');
                            doc.setFont('PretendardRegular', 'normal');
                            doc.text(T('건'), ...transform(doc, left, top + height - (height - 12) / 2), { align: 'left', baseline: 'bottom' });
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return rect;
                  },
                  async (left, top, _width, height) => {
                    // 복합혈당
                    const width = 75;
                    const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);
                    await vbox({
                      paddingLeft: 10,
                      paddingRight: 10,
                      paddingTop: 5,
                      paddingBottom: 5,
                      left,
                      top,
                      width,
                      height,
                      gap: 3,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 27;
                        await vbox({
                          left,
                          top,
                          width,
                          height,
                          gap: 1,
                        }, [
                          async (left, top, width, _height) => {
                            const height = 14;
                            await hbox({
                              left,
                              top,
                              width,
                              height,
                              gap: 3,
                            }, [
                              async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#6ED5E1' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                              async (left, top, width, height) => {
                                doc.setFontSize(pt(doc, 13));
                                doc.setTextColor('#666666');
                                doc.setFont('PretendardSemiBold', 'normal');
                                doc.text(T('복합혈당'), ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                                return roundedRect(doc, {}, left, top, width, height, 0, 0);
                              },
                            ]);
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                          async (left, top, width, _height) => {
                            const height = 12;
                            doc.setFontSize(pt(doc, 10));
                            doc.setTextColor('#666666');
                            doc.setFont('PretendardSemiBold', 'normal');
                            doc.text(T('(고+저혈당)'), ...transform(doc, left + 10, top + (height - 10) / 2), { align: 'left', baseline: 'top' });
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, width, _height) => {
                        const height = 17;
                        const gap = 3;

                        const [textWidth, valueWidth] = (() => {
                          doc.saveGraphicsState();
                          doc.setFontSize(pt(doc, 14));
                          doc.setFont('PretendardBold', 'normal');
                          const w1 = doc.getTextWidth((data.diet_data.complex_bld_sugr_cnt || 0).toString());
                          doc.setFontSize(pt(doc, 12));
                          doc.setFont('PretendardRegular', 'normal');
                          const w2 = doc.getTextWidth(T('건'));
                          doc.restoreGraphicsState();
                          return [w1 + w2 + gap, w1];
                        })();

                        await hbox({
                          left: left + (width - textWidth) / 2,
                          top,
                          width: textWidth,
                          height,
                          gap,
                        }, [
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 14));
                            doc.setTextColor('#333333');
                            doc.setFont('PretendardBold', 'normal');
                            doc.text((data.diet_data.complex_bld_sugr_cnt || 0).toString(), ...transform(doc, left + valueWidth, top + height - (height - 14) / 2), { align: 'right', baseline: 'bottom' });
                            doc.saveGraphicsState();
                            return roundedRect(doc, {}, left, top, valueWidth, height, 0, 0);
                          },
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 12));
                            doc.setTextColor('#999999');
                            doc.setFont('PretendardRegular', 'normal');
                            doc.text(T('건'), ...transform(doc, left, top + height - (height - 12) / 2), { align: 'left', baseline: 'bottom' });
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return rect;
                  },
                ]);
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
            ]);

            const position = transform(doc, left + width - 120, top + height - 120, 120, 120);
            await addImage(doc, 'diet_distrubution_doughnut_chart', data.diet_distrubution_doughnut_chart.url, ...position);

            return roundedRect(doc, {}, left, top, width, height, 0, 0);
          },
          async (_left, _top, _width, _height) => {
            // 식사 scatter 차트 레이아웃 강제 설정
            const left = _left + _width - 700;
            const top = _top - 220;
            const width = 700;
            const height = 200;
            const ratio = 700 / data.diet_distrubution_scatter_chart.width;

            const position = transform(doc, left, top, data.diet_distrubution_scatter_chart.width * ratio, data.diet_distrubution_scatter_chart.height * ratio);
            await addImage(doc, 'diet_distrubution_scatter_chart', data.diet_distrubution_scatter_chart.url, ...position);

            return roundedRect(doc, {}, left, top, width, height, 0, 0);
          },
        ]),
      ]);
      return roundedRect(doc, {}, left, top, 1120, 384, 0, 0)
    },
    async (left, top, width, height) => {
      const { totalHeight } = await vbox({
        left,
        top,
        width,
        height,
        gap: 20,
      }, [
        drawSectionBox(doc, data, 1120, 262, T('식사별 분석'), null, [
          async (left, top, width, height) => {
            // table

            const cellWidth = (1120 - 120 - 180) / 9;
            const columnWidths = [
              120,
              cellWidth,
              180,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth
            ];
            const headers = [
              T('날짜'),
              T('시간대'),
              T('대표음식'),
              T('이상혈당 발생'),
              T('혈당상승폭'),
              T('식전혈당'),
              T('최고혈당'),
              T('최고혈당 도달시간(분)'),
              T('2시간후 혈당'),
              T('식사전후 볼루스(U)'),
              T('식후 활동(보)'),
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            const body = (data.diet_meal_analysis_list.list || []).map((item, index) => {
              const {
                time,
                time_zone_tp_cd,
                main_food_nm,
                bldsugr_before_meal,
                postprand2h_max_bldsugr,
                postprand2h_bldsugr,
                postprand_abnrml_bldsugr_min,
                postprand2h_bldsugr_rise_w,
                postprand2h_bldsugr_tp,
                postprand2h_step_cnt,
                postprand2h_insulin_amt,
                bf_af_meal_insulin_amt,
              } = item;

              const luxon = fromFormat(time, 'yyyyMMddHHmmss');
              const labelDate = luxon.toFormat('yy.MM.dd');
              const labelWeekday = luxonClone(luxon).weekday;
              const labelTime = luxon.toFormat('HH:mm');

              return [{
                align: 'center',
                text: T('{0}({1}) {2}', labelDate, WEEK_DAY_COLUMNS[labelWeekday - 1], labelTime),
                width: columnWidths[0],
              }, {
                align: 'center',
                text: DAY_ZONE_TYPES[+time_zone_tp_cd - 1],
                width: columnWidths[1],
              }, {
                align: 'center',
                text: main_food_nm,
                width: columnWidths[2],
              }, {
                callback: (cell, options) => {
                  const {
                    left,
                    top,
                    width,
                    height,
                    textWidth,
                  } = options;
                  const fillColor = ABNORMAL_GLOCOSE_COLORS[cell.postprand2h_bldsugr_tp];
                  roundedRect(doc, { lineWidth: 1, fillColor }, left + cell.width / 2 + textWidth / 2 + 1, top + (height - 7) / 2, 7, 7, 3.5, 3.5);
                },
                align: 'center',
                text: ABNORMAL_GLOCOSE_TYPES[postprand2h_bldsugr_tp],
                width: columnWidths[3],
                postprand2h_bldsugr_tp,
              }, {
                align: 'center',
                text: (postprand2h_bldsugr_rise_w === 0 ? '' : Math.sign(postprand2h_bldsugr_rise_w) < 0 ? '-' : '+') + Math.abs(postprand2h_bldsugr_rise_w),
                width: columnWidths[4],
              }, {
                callback: color_callback(doc, 'B'),
                align: 'center',
                text: bldsugr_before_meal.toString(),
                width: columnWidths[5],
              }, {
                callback: color_callback(doc, 'B'),
                align: 'center',
                text: postprand2h_max_bldsugr.toString(),
                width: columnWidths[6],
              }, {
                align: 'center',
                text: postprand_abnrml_bldsugr_min.toString(),
                width: columnWidths[7],
              }, {
                callback: color_callback(doc, 'B'),
                align: 'center',
                text: postprand2h_bldsugr.toString(),
                width: columnWidths[8],
              }, {
                align: 'center',
                text: bf_af_meal_insulin_amt.toString(),
                width: columnWidths[9],
              }, {
                align: 'center',
                text: postprand2h_step_cnt.toString(),
                width: columnWidths[10],
              }]
            });

            const { totalHeight } = await vbox({
              left,
              top,
              width,
              height: 55,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              ...body.map((columns) => {
                return drawTableRow(doc, {}, 27, columns);
              }),
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#E2E7EF');
            doc.line(...transform(doc, left, top, left + 1120, top), 'S');
            doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, 1120, totalHeight, 0, 0);
          },
        ]),
        drawSectionBox(doc, data, 1120, 262, T('대표음식별 분석'), null, [
          async (left, top, width, height) => {
            // table

            const cellWidth = (1120 - 180 - 120) / 8;
            const columnWidths = [
              180,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              120,
            ];
            const headers = [
              T('대표음식'),
              T('기록(회)'),
              T('이상혈당 발생(건)'),
              T('이상혈당 발생(%)'),
              T('평균혈당 상승폭'),
              T('평균 최고혈당'),
              T('평균 칼로리(kcal)'),
              T('평균 식사량(g)'),
              T('평균 탄수화물(g)'),
              T('평균 탄단지(g)'),
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            const body = (data.diet_food_analysis_list.list || []).map((item, index) => {
              const {
                main_food_nm,
                intaken_cnt,
                avg_intaken_cal,
                avg_intaken_amt,
                carbo_intaken_amt,
                carbo_rate,
                protein_rate,
                fat_rate,
                postprand2h_avg_bldsugr_rise_w,
                postprand2h_avg_bldsugr_min,
                postprand2h_avg_bldsugr,
                postprand2h_avg_bldsugr_max,
                postprand2h_abnormal_cnt,
                postprand2h_abnormal_rate,
              } = item;

              return [{
                align: 'center',
                text: main_food_nm,
                width: columnWidths[0],
              }, {
                align: 'center',
                text: intaken_cnt.toString(),
                width: columnWidths[1],
              }, {
                align: 'center',
                text: postprand2h_abnormal_cnt.toString(),
                width: columnWidths[2],
              }, {
                align: 'center',
                text: postprand2h_abnormal_rate.toString(),
                width: columnWidths[3],
              }, {
                align: 'center',
                text: (postprand2h_avg_bldsugr_rise_w === 0 ? '' : Math.sign(postprand2h_avg_bldsugr_rise_w) < 0 ? '-' : '+') + Math.abs(postprand2h_avg_bldsugr_rise_w),
                width: columnWidths[4],
              }, {
                callback: color_callback(doc, 'B'),
                align: 'center',
                text: postprand2h_avg_bldsugr.toString(),
                width: columnWidths[5],
              }, {
                align: 'center',
                text: avg_intaken_cal.toString(),
                width: columnWidths[6],
              }, {
                align: 'center',
                text: avg_intaken_amt.toString(),
                width: columnWidths[7],
              }, {
                align: 'center',
                text: carbo_intaken_amt.toString(),
                width: columnWidths[8],
              }, {
                align: 'center',
                text: T('{0}-{1}-{2}', carbo_rate, protein_rate, fat_rate),
                width: columnWidths[9],
              }]
            });

            const { totalHeight } = await vbox({
              left,
              top,
              width,
              height: 55,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              ...body.map((columns) => {
                return drawTableRow(doc, {}, 27, columns);
              }),
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#E2E7EF');
            doc.line(...transform(doc, left, top, left + 1120, top), 'S');
            doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, 1120, totalHeight, 0, 0);
          },
        ]),
      ]);
      return roundedRect(doc, {}, left, top, 1120, totalHeight, 0, 0);
    },
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage4(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    drawSectionBox(doc, data, 1120, 640, T('일별 통계'), null, [
      async (left, top, width, height) => {
        await vbox({
          left,
          top,
          width,
          height,
          gap: 20,
        }, [
          async (left, top, width, height) => {
            const ratio = 1120 / data.diet_statistics_chart.width;
            const position = transform(doc, left, top, data.diet_statistics_chart.width * ratio, data.diet_statistics_chart.height * ratio);
            await addImage(doc, 'diet_statistics_chart', data.diet_statistics_chart.url, ...position);
            return roundedRect(doc, {}, left, top, 1120, 300, 0, 0);
          },
          async (left, top, width, height) => {
            // table

            const cellWidth = (1120) / 7;
            const columnWidths = [
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
            ];
            const headers = [
              T('날짜'),
              T('기록(회)'),
              T('칼로리(kcal)'),
              T('탄수화물(g)'),
              T('단백질(g)'),
              T('지방(g)'),
              T('탄단지 비율(%)'),
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            const body = (data.diet_daily_statistics.meal_stat_list || []).map((item, index) => {
              const {
                time,
                avg_cal,
                avg_carbo_cal,
                avg_carbo_rate,
                avg_fat_cal,
                avg_fat_rate,
                avg_protein_cal,
                avg_protein_rate,
                day_tp_cd,
                total_intaken_cnt,
              } = item;
              const luxon = fromFormat(time, 'yyyyMMdd');
              const date = luxon.toFormat("yy.MM.dd");
              const weekday = luxonClone(luxon).weekday;
              return [{
                align: 'center',
                text: T('{0}({1})', date, WEEK_DAY_COLUMNS[weekday - 1]),
                width: columnWidths[0],
              }, {
                align: 'center',
                text: numberWithComma(total_intaken_cnt),
                width: columnWidths[1],
              }, {
                align: 'center',
                text: numberWithComma(avg_cal),
                width: columnWidths[2],
              }, {
                align: 'center',
                text: numberWithComma(avg_carbo_cal),
                width: columnWidths[3],
              }, {
                align: 'center',
                text: numberWithComma(avg_protein_cal),
                width: columnWidths[4],
              }, {
                align: 'center',
                text: numberWithComma(avg_fat_cal),
                width: columnWidths[5],
              }, {
                align: 'center',
                text: T('{0}-{1}-{2}', avg_carbo_rate, avg_protein_rate, avg_fat_rate),
                width: columnWidths[6],
              }]
            });

            const { totalHeight } = await vbox({
              left,
              top,
              width,
              height: 55,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              drawTableRow(doc, {}, 27, [{
                align: 'center',
                text: T('전체 (일평균)'),
                width: columnWidths[0],
              }, {
                align: 'center',
                text: numberWithComma(data.diet_daily_statistics.tot_total_intaken_cnt),
                width: columnWidths[1],
              }, {
                align: 'center',
                text: numberWithComma(data.diet_daily_statistics.tot_avg_cal),
                width: columnWidths[2],
              }, {
                align: 'center',
                text: numberWithComma(data.diet_daily_statistics.tot_avg_carbo_cal),
                width: columnWidths[3],
              }, {
                align: 'center',
                text: numberWithComma(data.diet_daily_statistics.tot_avg_protein_cal),
                width: columnWidths[4],
              }, {
                align: 'center',
                text: numberWithComma(data.diet_daily_statistics.tot_avg_fat_cal),
                width: columnWidths[5],
              }, {
                align: 'center',
                text: T('{0}-{1}-{2}', data.diet_daily_statistics.tot_avg_carbo_rate, data.diet_daily_statistics.tot_avg_protein_rate, data.diet_daily_statistics.tot_avg_fat_rate),
                width: columnWidths[6],
              }]),
              ...body.map((columns) => {
                return drawTableRow(doc, {}, 27, columns);
              }),
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#E2E7EF');
            doc.line(...transform(doc, left, top, left + 1120, top), 'S');
            doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, 1120, totalHeight, 0, 0);
          },
        ]);

        return roundedRect(doc, {}, left, top, width, height, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage5(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    async (left, top, width, height) => {
      await hbox({
        left,
        top,
        width,
        height,
        gap: 10,
      }, [
        drawSummaryBox(doc, data, 326, 124, T('일평균 활동'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('걸음수'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.steps_data.daily_avg_step_cnt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('보'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 2 - 10, top, left + width / 2 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 2, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('활동시간'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.steps_data.daily_avg_active_time || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('분'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width / 2, 50, 0, 0)
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 506, 124, T('식후 평균 활동'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('걸음수'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.steps_data.meal_2hr_after_avg_step_cnt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('보'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('활동시간'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.steps_data.meal_2hr_after_avg_active_time || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('분'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('혈당 감소'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = '-' + numberWithComma(data.steps_data.meal_2hr_after_avg_bld_sugr_decrease_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('mg/dL'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 268, 124, T('권장 목표 달성'), null, [
          async (left, top, width, height) => {
            await drawSummaryValue(doc, { left, top, width, height }, T('5000보 이상'), [
              async (left, top, _width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 28));
                doc.setTextColor('##333333');
                doc.setFont('PretendardBold', 'normal');
                const text = numberWithComma(data.steps_data.active_days_over_5000 || 0);
                const width = doc.getTextWidth(text) / ratio(doc);
                doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
              async (left, top, _width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 12));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('일'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
            ]);

            return roundedRect(doc, {}, left, top, width, 50, 0, 0)
          },
        ]),
      ]);
      return roundedRect(doc, {}, left, top, 1120, 124, 0, 0);
    },
    drawSectionBox(doc, data, 1120, 454, T('활동분석 (day)'), null, [
      async (left, top, width, height) => {
        await vbox({
          left,
          top,
          width,
          height,
          gap: 20,
        }, [
          async (left, top, width, height) => {
            const ratio = 1120 / data.step_analysis_day_chart.width;
            const position = transform(doc, left, top, data.step_analysis_day_chart.width * ratio, data.step_analysis_day_chart.height * ratio);
            await addImage(doc, 'step_analysis_day_chart', data.step_analysis_day_chart.url, ...position);

            return roundedRect(doc, {}, left, top, 1120, 300, 0, 0);
          },
          async (left, top, width, height) => {
            // table

            const cellWidth = (1120) / 5;
            const columnWidths = [
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth
            ];
            const headers = [
              T('구분'),
              T('새벽(0-6H)'),
              T('오전(6-12H)'),
              T('오후(12-18H)'),
              T('저녁(18-24H)')
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            await vbox({
              left,
              top,
              width,
              height: 55,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              drawTableRow(doc, {}, 27, [
                T('시간대별 평균 걸음수 (보)'),
                ...data.steps_data.active_pattern_time_zone_list.map((item) => numberWithComma(item.avg_step_cnt || '-')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
              drawTableRow(doc, {}, 27, [
                T('평균 심박수 (bpm'),
                ...data.steps_data.active_pattern_time_zone_list.map((item) => numberWithComma(item.avg_heart_rate || '-')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#E2E7EF');
            doc.line(...transform(doc, left, top, left + 1120, top), 'S');
            doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, 1120, 102, 0, 0);
          },
        ]);
        return roundedRect(doc, {}, left, top, 1120, 407, 0, 0);
      },
    ]),
    drawSectionBox(doc, data, 1120, 364, T('활동분석 (week)'), null, [
      async (left, top, width, height) => {
        await vbox({
          left,
          top,
          width,
          height,
          gap: 20,
        }, [
          async (left, top, width, height) => {
            const ratio = 1120 / data.step_analysis_week_chart.width;
            const position = transform(doc, left, top, data.step_analysis_week_chart.width * ratio, data.step_analysis_week_chart.height * ratio);
            await addImage(doc, 'step_analysis_week_chart', data.step_analysis_week_chart.url, ...position);

            return roundedRect(doc, {}, left, top, 1120, data.step_analysis_week_chart.height * ratio, 0, 0);
          },
          async (left, top, width, height) => {

            await hbox({
              left: left + width / 2 - 426 / 2,
              top,
              width: 425,
              height: 13,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setGState(new doc.GState({ 'opacity': 0.5 }));
                const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#0070FF' }, left, top + (18 - 10) / 2, 10, 10, 5, 5);
                doc.restoreGraphicsState();
                return rect;
              },
              async (left, top, width, height) => {
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#667085');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('걸음수 (5,000보 이상이면 100% 표시)'), ...transform(doc, left + 5, top + (35 - 28) / 2), { align: 'left', baseline: 'top', });
                return roundedRect(doc, {}, left, top, 203, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setGState(new doc.GState({ 'opacity': 0.5 }));
                const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#FF3C5F' }, left, top + (18 - 10) / 2, 10, 10, 5, 5);
                doc.restoreGraphicsState();
                return rect;
              },
              async (left, top, width, height) => {
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#667085');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('평균혈당 (250mg/dL 이상이면 100% 표시)'), ...transform(doc, left + 5, top + (35 - 28) / 2), { align: 'left', baseline: 'top', });
                return roundedRect(doc, {}, left, top, 203, 13, 0, 0);
              },
            ]);

            return roundedRect(doc, {}, left, top, 1120, 18, 0, 0);
          },
          async (left, top, width, height) => {
            // table

            const cellWidth = (1120) / 10;
            const columnWidths = [
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth
            ];
            const headers = [
              T(''),
              T('월'),
              T('화'),
              T('수'),
              T('목'),
              T('금'),
              T('토'),
              T('일'),
              T('주중'),
              T('주말'),
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            await vbox({
              left,
              top,
              width,
              height: 55,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              drawTableRow(doc, {}, 27, [
                T('평균 걸음수 (보)'),
                ...data.steps_data.active_analyze_day_list.map((item) => numberWithComma(item.avg_step_cnt || '-')),
                ...data.steps_data.active_analyze_week_days_weekend_list.map((item) => numberWithComma(item.avg_step_cnt || '-')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
              drawTableRow(doc, {}, 27, [
                T('평균 활동시간 (분)'),
                ...data.steps_data.active_analyze_day_list.map((item) => numberWithComma(item.avg_active_time || '-')),
                ...data.steps_data.active_analyze_week_days_weekend_list.map((item) => numberWithComma(item.avg_active_time || '-')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
              drawTableRow(doc, {}, 27, [
                T('평균혈당 (mg/dL)'),
                ...data.steps_data.active_analyze_day_list.map((item) => item.avg_bld_sugr.toString()),
                ...data.steps_data.active_analyze_week_days_weekend_list.map((item) => item.avg_bld_sugr.toString()),
              ].map((text, index) => ({
                callback: color_callback(doc, 'B'),
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#E2E7EF');
            doc.line(...transform(doc, left, top, left + 1120, top), 'S');
            doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, 1120, 134, 0, 0);
          },
        ])
        return roundedRect(doc, {}, left, top, 1120, 307, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage6(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    drawSectionBox(doc, data, 1120, 588, T('일별 통계'), null, [
      async (left, top, width, height) => {
        const ratio = 1120 / data.step_statistics_chart.width;
        const position = transform(doc, left, top, data.step_statistics_chart.width * ratio, data.step_statistics_chart.height * ratio);
        await addImage(doc, 'step_statistics_chart', data.step_statistics_chart.url, ...position);

        return roundedRect(doc, {}, left, top, 1120, 300, 0, 0);
      },
      async (left, top, width, height) => {
        // table

        const cellWidth = (1120) / 3;
        const columnWidths = [
          cellWidth,
          cellWidth,
          cellWidth
        ];
        const headers = [
          T('날짜'),
          T('걸음수 (보)'),
          T('활동시간 (분)'),
        ].map((text, index) => ({
          text,
          width: columnWidths[index],
        }));

        await vbox({
          left,
          top,
          width,
          height: 55,
        }, [
          drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
          ...data.exercise_daily_statistics_list.list.map((item) => {
            const {
              date,
              step_cnt,
              active_time,
              burned_calorie,
            } = item;
            const luxon = fromFormat(date, 'yyyyMMdd');
            const weekday = luxonClone(luxon).weekday
            const columns = [
              T('{0} ({1})', luxon.toFormat('yy.MM.dd'), WEEK_DAY_COLUMNS[weekday - 1]),
              numberWithComma(step_cnt || 0),
              active_time
            ].map(((text, index) => ({
              align: 'center',
              text,
              width: columnWidths[index],
            })));
            return drawTableRow(doc, {}, 27, columns);
          }),
        ]);

        doc.saveGraphicsState();
        doc.setLineWidth(transform(doc, 1));
        doc.setDrawColor('#E2E7EF');
        doc.line(...transform(doc, left, top, left + 1120, top), 'S');
        doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, 1120, 200, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage7(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    async (left, top, width, height) => {
      await hbox({
        left,
        top,
        width,
        height,
        gap: 10,
      }, [
        drawSummaryBox(doc, data, 326, 124, T('일평균 활동'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('소모칼로리'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.calories_data.daily_avg_cal_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('보'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 2 - 10, top, left + width / 2 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 2, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('활동시간'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.calories_data.daily_avg_active_time || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('분'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width / 2, 50, 0, 0)
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 506, 124, T('식후 평균 활동'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('소모칼로리'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.calories_data.meal_2hr_after_avg_cal_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('보'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('활동시간'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.calories_data.meal_2hr_after_avg_active_time || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('분'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('혈당 감소'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = '-' + numberWithComma(data.calories_data.meal_2hr_after_avg_bld_sugr_decrease_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('mg/dL'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 268, 124, T('권장 목표 달성'), null, [
          async (left, top, width, height) => {
            await drawSummaryValue(doc, { left, top, width, height }, T('5000보 이상'), [
              async (left, top, _width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 28));
                doc.setTextColor('##333333');
                doc.setFont('PretendardBold', 'normal');
                const text = numberWithComma(data.steps_data.active_days_over_5000 || 0);
                const width = doc.getTextWidth(text) / ratio(doc);
                doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
              async (left, top, _width, height) => {
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 12));
                doc.setTextColor('#999999');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('일'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
            ]);

            return roundedRect(doc, {}, left, top, width, 50, 0, 0)
          },
        ]),
      ]);
      return roundedRect(doc, {}, left, top, 1120, 124, 0, 0);
    },
    drawSectionBox(doc, data, 1120, 454, T('활동분석 (day)'), null, [
      async (left, top, width, height) => {
        await vbox({
          left,
          top,
          width,
          height,
          gap: 20,
        }, [
          async (left, top, width, height) => {
            const ratio = 1120 / data.calory_analysis_day_chart.width;
            const position = transform(doc, left, top, data.calory_analysis_day_chart.width * ratio, data.calory_analysis_day_chart.height * ratio);
            await addImage(doc, 'calory_analysis_day_chart', data.calory_analysis_day_chart.url, ...position);

            return roundedRect(doc, {}, left, top, 1120, 300, 0, 0);
          },
          async (left, top, width, height) => {
            // table

            const cellWidth = (1120) / 5;
            const columnWidths = [
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth
            ];
            const headers = [
              T('구분'),
              T('새벽(0-6H)'),
              T('오전(6-12H)'),
              T('오후(12-18H)'),
              T('저녁(18-24H)')
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            await vbox({
              left,
              top,
              width,
              height: 55,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              drawTableRow(doc, {}, 27, [
                T('시간대별 평균 소모칼로리 (kcal)'),
                ...data.calories_data.active_pattern_time_zone_list.map((item) => numberWithComma(item.avg_cal_amt || '-')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
              drawTableRow(doc, {}, 27, [
                T('평균 심박수 (bpm'),
                ...data.calories_data.active_pattern_time_zone_list.map((item) => numberWithComma(item.avg_heart_rate || '-')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#E2E7EF');
            doc.line(...transform(doc, left, top, left + 1120, top), 'S');
            doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, 1120, 102, 0, 0);
          },
        ]);
        return roundedRect(doc, {}, left, top, 1120, 407, 0, 0);
      },
    ]),
    drawSectionBox(doc, data, 1120, 364, T('활동분석 (week)'), null, [
      async (left, top, width, height) => {
        await vbox({
          left,
          top,
          width,
          height,
          gap: 20,
        }, [
          async (left, top, width, height) => {
            const ratio = 1120 / data.calory_analysis_week_chart.width;
            const position = transform(doc, left, top, data.calory_analysis_week_chart.width * ratio, data.calory_analysis_week_chart.height * ratio);
            await addImage(doc, 'calory_analysis_week_chart', data.calory_analysis_week_chart.url, ...position);

            return roundedRect(doc, {}, left, top, 1120, data.calory_analysis_week_chart.height * ratio, 0, 0);
          },
          async (left, top, width, height) => {
            await hbox({
              left: left + width / 2 - 426 / 2,
              top,
              width: 425,
              height: 13,
            }, [
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setGState(new doc.GState({ 'opacity': 0.5 }));
                const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#0DCE74' }, left, top + (18 - 10) / 2, 10, 10, 5, 5);
                doc.restoreGraphicsState();
                return rect;
              },
              async (left, top, width, height) => {
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#667085');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('소모칼로리 (200kcal 이상이면 100% 표시)'), ...transform(doc, left + 5, top + (35 - 28) / 2), { align: 'left', baseline: 'top', });
                return roundedRect(doc, {}, left, top, 203, 13, 0, 0);
              },
              async (left, top, width, height) => {
                doc.saveGraphicsState();
                doc.setGState(new doc.GState({ 'opacity': 0.5 }));
                const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#FF3C5F' }, left, top + (18 - 10) / 2, 10, 10, 5, 5);
                doc.restoreGraphicsState();
                return rect;
              },
              async (left, top, width, height) => {
                doc.setFontSize(pt(doc, 11));
                doc.setTextColor('#667085');
                doc.setFont('PretendardRegular', 'normal');
                doc.text(T('평균혈당 (250mg/dL 이상이면 100% 표시)'), ...transform(doc, left + 5, top + (35 - 28) / 2), { align: 'left', baseline: 'top', });
                return roundedRect(doc, {}, left, top, 203, 13, 0, 0);
              },
            ]);

            return roundedRect(doc, {}, left, top, 1120, 18, 0, 0);
          },
          async (left, top, width, height) => {
            // table

            const cellWidth = (1120) / 10;
            const columnWidths = [
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth,
              cellWidth
            ];
            const headers = [
              T(''),
              T('월'),
              T('화'),
              T('수'),
              T('목'),
              T('금'),
              T('토'),
              T('일'),
              T('주중'),
              T('주말'),
            ].map((text, index) => ({
              text,
              width: columnWidths[index],
            }));

            await vbox({
              left,
              top,
              width,
              height: 55,
            }, [
              drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
              drawTableRow(doc, {}, 27, [
                T('평균 소모칼로리 (kcal)'),
                ...data.calories_data.active_analyze_day_list.map((item) => numberWithComma(item.avg_cal_amt || '')),
                ...data.calories_data.active_analyze_week_days_weekend_list.map((item) => numberWithComma(item.avg_cal_amt || '')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
              drawTableRow(doc, {}, 27, [
                T('평균 활동시간 (분)'),
                ...data.calories_data.active_analyze_day_list.map((item) => numberWithComma(item.avg_active_time || '-')),
                ...data.calories_data.active_analyze_week_days_weekend_list.map((item) => numberWithComma(item.avg_active_time || '-')),
              ].map((text, index) => ({
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
              drawTableRow(doc, {}, 27, [
                T('평균혈당 (mg/dL)'),
                ...data.calories_data.active_analyze_day_list.map((item) => item.avg_bld_sugr.toString()),
                ...data.calories_data.active_analyze_week_days_weekend_list.map((item) => item.avg_bld_sugr.toString()),
              ].map((text, index) => ({
                callback: color_callback(doc, 'B'),
                align: 'center',
                text,
                width: columnWidths[index],
              }))),
            ]);

            doc.saveGraphicsState();
            doc.setLineWidth(transform(doc, 1));
            doc.setDrawColor('#E2E7EF');
            doc.line(...transform(doc, left, top, left + 1120, top), 'S');
            doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
            doc.restoreGraphicsState();

            return roundedRect(doc, {}, left, top, 1120, 134, 0, 0);
          },
        ])
        return roundedRect(doc, {}, left, top, 1120, 307, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage8(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    drawSectionBox(doc, data, 1120, 588, T('일별 통계'), null, [
      async (left, top, width, height) => {
        const ratio = 1120 / data.calory_statistics_chart.width;
        const position = transform(doc, left, top, data.calory_statistics_chart.width * ratio, data.calory_statistics_chart.height * ratio);
        await addImage(doc, 'calory_statistics_chart', data.calory_statistics_chart.url, ...position);

        return roundedRect(doc, {}, left, top, 1120, 300, 0, 0);
      },
      async (left, top, width, height) => {
        // table

        const cellWidth = (1120) / 3;
        const columnWidths = [
          cellWidth,
          cellWidth,
          cellWidth
        ];
        const headers = [
          T('날짜'),
          T('소모칼로리 (kcal)'),
          T('활동시간 (분)'),
        ].map((text, index) => ({
          text,
          width: columnWidths[index],
        }));

        await vbox({
          left,
          top,
          width,
          height: 55,
        }, [
          drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
          ...data.exercise_daily_statistics_list.list.map((item) => {
            const {
              date,
              step_cnt,
              active_time,
              burned_calorie,
            } = item;
            const luxon = fromFormat(date, 'yyyyMMdd');
            const weekday = luxonClone(luxon).weekday
            const columns = [
              T('{0} ({1})', luxon.toFormat('yy.MM.dd'), WEEK_DAY_COLUMNS[weekday - 1]),
              numberWithComma(burned_calorie || 0),
              active_time
            ].map(((text, index) => ({
              align: 'center',
              text,
              width: columnWidths[index],
            })));
            return drawTableRow(doc, {}, 27, columns);
          }),
        ]);

        doc.saveGraphicsState();
        doc.setLineWidth(transform(doc, 1));
        doc.setDrawColor('#E2E7EF');
        doc.line(...transform(doc, left, top, left + 1120, top), 'S');
        doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, 1120, 200, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage9(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    async (left, top, width, height) => {
      await hbox({
        left,
        top,
        width,
        height,
        gap: 10,
      }, [
        drawSummaryBox(doc, data, 506, 124, T('일평균 인슐린'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('전체'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.insulin_data.avg_insulin_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('kcal'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('볼루스'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.insulin_data.avg_bolus_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('U'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('기저'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.insulin_data.avg_giju_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('U'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 348, 124, T('일별 인슐린'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('최대'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.insulin_data.avg_max_insulin_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('U'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                doc.saveGraphicsState();
                doc.setLineWidth(transform(doc, 1));
                doc.setDrawColor('#D8D8D8');
                doc.setLineDashPattern(transform(doc, 2, 2), 0);
                doc.line(...transform(doc, left + width / 3 - 10, top, left + width / 3 - 10, top + 50), 'S');
                doc.restoreGraphicsState();

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('최소'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.insulin_data.avg_min_insulin_amt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('U'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
        drawSummaryBox(doc, data, 165, 124, T('일평균 기록'), null, [
          async (left, top, width, height) => {
            await hbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, height) => {
                await drawSummaryValue(doc, { left, top, width, height }, T('볼루스'), [
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 28));
                    doc.setTextColor('##333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = numberWithComma(data.insulin_data.avg_bolus_cnt || 0);
                    const width = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, _width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    doc.text(T('U'), ...transform(doc, left + 3, top + (height - 12) / 2), { align: 'left', baseline: 'top' });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);

                return roundedRect(doc, {}, left, top, width / 3, 50, 0, 0)
              },
            ]);
            return roundedRect(doc, {}, left, top, width, 50, 0, 0);
          },
        ]),
      ]);
      return roundedRect(doc, {}, left, top, 1120, 124, 0, 0);
    },
    drawSummaryLinkBox(doc, data, 1120, 240, T('인슐린 분포'), null, [
      async (left, top, _width, _height) => {
        const width = 380;
        const height = 200 - 14 - 20;

        await vbox({
          top: top + height - 128,
          left,
          width: 213,
          height: 128,
          gap: 20,
        }, [
          async (left, top, width, _height) => {
            const height = 54;
            await vbox({
              left,
              top,
              width,
              height,
            }, [
              async (left, top, width, _height) => {
                const height = 14;
                doc.saveGraphicsState();
                doc.setFontSize(pt(doc, 13));
                doc.setTextColor('#666666');
                doc.setFont('PretendardSemiBold', 'normal');
                const text = T('볼루스후 이상혈당 발생');
                doc.text(text, ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top', });
                doc.restoreGraphicsState();
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
              async (left, top, width, _height) => {
                const height = 40;
                await hbox({
                  left,
                  top,
                  width,
                  height,
                }, [
                  async (left, top, width, height) => {
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 32));
                    doc.setTextColor('#333333');
                    doc.setFont('PretendardBold', 'normal');
                    const text = (data.insulin_data.after_bolus_abnormal_rate || 0).toString();
                    const textWidth = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top + height - (height - 32) / 2), { align: 'left', baseline: 'bottom', });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, textWidth, height, 0, 0);
                  },
                  async (_left, top, width, height) => {
                    const left = _left + 5;
                    doc.saveGraphicsState();
                    doc.setFontSize(pt(doc, 12));
                    doc.setTextColor('#999999');
                    doc.setFont('PretendardRegular', 'normal');
                    const text = T('%');
                    const textWidth = doc.getTextWidth(text) / ratio(doc);
                    doc.text(text, ...transform(doc, left, top + height - (height - 32) / 2), { align: 'left', baseline: 'bottom', });
                    doc.restoreGraphicsState();
                    return roundedRect(doc, {}, left, top, textWidth, height, 0, 0);
                  },
                ])
                return roundedRect(doc, {}, left, top, width, height, 0, 0);
              },
            ]);
            return roundedRect(doc, {}, left, top, width, height, 0, 0);
          },
          async (left, top, width, _height) => {
            const height = 54;
            await hbox({
              left,
              top,
              width,
              height,
              gap: 5,
            }, [
              async (left, top, _width, height) => {
                // 고혈당
                const width = 64;
                const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);
                await vbox({
                  paddingLeft: 10,
                  paddingRight: 10,
                  paddingTop: 5,
                  paddingBottom: 5,
                  left,
                  top,
                  width,
                  height,
                  gap: 3,
                }, [
                  async (left, top, width, _height) => {
                    const height = 27;
                    await vbox({
                      left,
                      top,
                      width,
                      height,
                      gap: 1,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 14;
                        await hbox({
                          left,
                          top,
                          width,
                          height,
                          gap: 3,
                        }, [
                          async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#FF995A' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                          async (left, top, width, height) => {
                            doc.saveGraphicsState();
                            doc.setFontSize(pt(doc, 13));
                            doc.setTextColor('#666666');
                            doc.setFont('PretendardSemiBold', 'normal');
                            doc.text(T('고혈당'), ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                            doc.saveGraphicsState();
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, width, _height) => {
                    const height = 17;
                    const gap = 3;

                    const [textWidth, valueWidth] = (() => {
                      doc.saveGraphicsState();
                      doc.setFontSize(pt(doc, 14));
                      doc.setFont('PretendardBold', 'normal');
                      const w1 = doc.getTextWidth((data.insulin_data.high_bld_sugr_cnt || 0).toString());
                      doc.setFontSize(pt(doc, 12));
                      doc.setFont('PretendardRegular', 'normal');
                      const w2 = doc.getTextWidth(T('건'));
                      doc.restoreGraphicsState();
                      return [w1 + w2 + gap, w1];
                    })();

                    await hbox({
                      left: left + (width - textWidth) / 2,
                      top,
                      width: textWidth,
                      height,
                      gap,
                    }, [
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 14));
                        doc.setTextColor('#333333');
                        doc.setFont('PretendardBold', 'normal');
                        doc.text((data.insulin_data.high_bld_sugr_cnt || 0).toString(), ...transform(doc, left + valueWidth, top + height - (height - 14) / 2), { align: 'right', baseline: 'bottom' });
                        doc.saveGraphicsState();
                        return roundedRect(doc, {}, left, top, valueWidth, height, 0, 0);
                      },
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('건'), ...transform(doc, left, top + height - (height - 12) / 2), { align: 'left', baseline: 'bottom' });
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);
                return rect;
              },
              async (left, top, _width, height) => {
                // 저혈당
                const width = 64;
                const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);
                await vbox({
                  paddingLeft: 10,
                  paddingRight: 10,
                  paddingTop: 5,
                  paddingBottom: 5,
                  left,
                  top,
                  width,
                  height,
                  gap: 3,
                }, [
                  async (left, top, width, _height) => {
                    const height = 27;
                    await vbox({
                      left,
                      top,
                      width,
                      height,
                      gap: 1,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 14;
                        await hbox({
                          left,
                          top,
                          width,
                          height,
                          gap: 3,
                        }, [
                          async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#756CE4' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                          async (left, top, width, height) => {
                            doc.setFontSize(pt(doc, 13));
                            doc.setTextColor('#666666');
                            doc.setFont('PretendardSemiBold', 'normal');
                            doc.text(T('저혈당'), ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, width, _height) => {
                    const height = 17;
                    const gap = 3;

                    const [textWidth, valueWidth] = (() => {
                      doc.saveGraphicsState();
                      doc.setFontSize(pt(doc, 14));
                      doc.setFont('PretendardBold', 'normal');
                      const w1 = doc.getTextWidth((data.insulin_data.low_bld_sugr_cnt || 0).toString());
                      doc.setFontSize(pt(doc, 12));
                      doc.setFont('PretendardRegular', 'normal');
                      const w2 = doc.getTextWidth(T('건'));
                      doc.restoreGraphicsState();
                      return [w1 + w2 + gap, w1];
                    })();

                    await hbox({
                      left: left + (width - textWidth) / 2,
                      top,
                      width: textWidth,
                      height,
                      gap,
                    }, [
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 14));
                        doc.setTextColor('#333333');
                        doc.setFont('PretendardBold', 'normal');
                        doc.text((data.insulin_data.low_bld_sugr_cnt || 0).toString(), ...transform(doc, left + valueWidth, top + height - (height - 14) / 2), { align: 'right', baseline: 'bottom' });
                        doc.saveGraphicsState();
                        return roundedRect(doc, {}, left, top, valueWidth, height, 0, 0);
                      },
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('건'), ...transform(doc, left, top + height - (height - 12) / 2), { align: 'left', baseline: 'bottom' });
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);
                return rect;
              },
              async (left, top, _width, height) => {
                // 복합혈당
                const width = 75;
                const rect = roundedRect(doc, { lineWidth: 1, fillColor: '#F6F7FB' }, left, top, width, height, 5, 5);
                await vbox({
                  paddingLeft: 10,
                  paddingRight: 10,
                  paddingTop: 5,
                  paddingBottom: 5,
                  left,
                  top,
                  width,
                  height,
                  gap: 3,
                }, [
                  async (left, top, width, _height) => {
                    const height = 27;
                    await vbox({
                      left,
                      top,
                      width,
                      height,
                      gap: 1,
                    }, [
                      async (left, top, width, _height) => {
                        const height = 14;
                        await hbox({
                          left,
                          top,
                          width,
                          height,
                          gap: 3,
                        }, [
                          async (left, top, width, height) => roundedRect(doc, { lineWidth: 1, fillColor: '#6ED5E1' }, left, top + (height - 7) / 2, 7, 7, 3.5, 3.5),
                          async (left, top, width, height) => {
                            doc.setFontSize(pt(doc, 13));
                            doc.setTextColor('#666666');
                            doc.setFont('PretendardSemiBold', 'normal');
                            doc.text(T('복합혈당'), ...transform(doc, left, top + (height - 13) / 2), { align: 'left', baseline: 'top' });
                            return roundedRect(doc, {}, left, top, width, height, 0, 0);
                          },
                        ]);
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                      async (left, top, width, _height) => {
                        const height = 12;
                        doc.setFontSize(pt(doc, 10));
                        doc.setTextColor('#666666');
                        doc.setFont('PretendardSemiBold', 'normal');
                        doc.text(T('(고+저혈당)'), ...transform(doc, left + 10, top + (height - 10) / 2), { align: 'left', baseline: 'top' });
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                  async (left, top, width, _height) => {
                    const height = 17;
                    const gap = 3;

                    const [textWidth, valueWidth] = (() => {
                      doc.saveGraphicsState();
                      doc.setFontSize(pt(doc, 14));
                      doc.setFont('PretendardBold', 'normal');
                      const w1 = doc.getTextWidth((data.insulin_data.complex_bld_sugr_cnt || 0).toString());
                      doc.setFontSize(pt(doc, 12));
                      doc.setFont('PretendardRegular', 'normal');
                      const w2 = doc.getTextWidth(T('건'));
                      doc.restoreGraphicsState();
                      return [w1 + w2 + gap, w1];
                    })();

                    await hbox({
                      left: left + (width - textWidth) / 2,
                      top,
                      width: textWidth,
                      height,
                      gap,
                    }, [
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 14));
                        doc.setTextColor('#333333');
                        doc.setFont('PretendardBold', 'normal');
                        doc.text((data.insulin_data.complex_bld_sugr_cnt || 0).toString(), ...transform(doc, left + valueWidth, top + height - (height - 14) / 2), { align: 'right', baseline: 'bottom' });
                        doc.saveGraphicsState();
                        return roundedRect(doc, {}, left, top, valueWidth, height, 0, 0);
                      },
                      async (left, top, width, height) => {
                        doc.saveGraphicsState();
                        doc.setFontSize(pt(doc, 12));
                        doc.setTextColor('#999999');
                        doc.setFont('PretendardRegular', 'normal');
                        doc.text(T('건'), ...transform(doc, left, top + height - (height - 12) / 2), { align: 'left', baseline: 'bottom' });
                        return roundedRect(doc, {}, left, top, width, height, 0, 0);
                      },
                    ]);
                    return roundedRect(doc, {}, left, top, width, height, 0, 0);
                  },
                ]);
                return rect;
              },
            ]);
            return roundedRect(doc, {}, left, top, width, height, 0, 0);
          },
        ]);

        const position = transform(doc, left + width - 120, top + height - 120, 120, 120);
        await addImage(doc, 'insulin_distrubution_doughnut_chart', data.insulin_distrubution_doughnut_chart.url, ...position);

        return roundedRect(doc, {}, left, top, width, height, 0, 0);
      },
      async (_left, _top, _width, _height) => {
        // 식사 scatter 차트 레이아웃 강제 설정
        const left = _left + _width - 700;
        const top = _top - 220;
        const width = 700;
        const height = 200;
        const ratio = 700 / data.insulin_distrubution_scatter_chart.width;

        const position = transform(doc, left, top, data.insulin_distrubution_scatter_chart.width * ratio, data.insulin_distrubution_scatter_chart.height * ratio);
        await addImage(doc, 'insulin_distrubution_scatter_chart', data.insulin_distrubution_scatter_chart.url, ...position);

        return roundedRect(doc, {}, left, top, width, height, 0, 0);
      },
    ]),
    drawSectionBox(doc, data, 1120, 234, T('볼루스 분석'), null, [
      async (left, top, width, height) => {
        // table

        const cellWidth = (1120 - 180) / 7;
        const columnWidths = [
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          180,
          cellWidth,
          cellWidth
        ];
        const headers = [
          T('날짜'),
          T('시간대'),
          T('투여량'),
          T('투여시점 혈당'),
          T('이상혈당 발생'),
          T('이상혈당 발생까지 시간(분)'),
          T('1시간내 식사여부'),
          T('2시간내 활동(보)'),
        ].map((text, index) => ({
          text,
          width: columnWidths[index],
        }));

        const body = (data.insulin_bolus_analysis_list.list || []).map((item, index) => {
          const {
            time,
            day_tp_cd,
            time_zone_tp_cd,
            bolus_amt,
            bldsugr,
            abnormal_bldsugr_maintain_time,
            abnormal_bldsugr_tp,
            before_abnormal_bldsugr_meal_yn,
            before_abnormal_bldsugr_active_min,
          } = item;

          const luxon = fromFormat(time, 'yyyyMMddhhmmss');
          const date = luxon.toFormat("yy.MM.dd");
          const weekday = luxonClone(luxon).weekday;

          return [{
            align: 'center',
            text: T('{0}({1})', date, WEEK_DAY_COLUMNS[weekday - 1]),
            width: columnWidths[0],
          }, {
            align: 'center',
            text: DAY_ZONE_TYPES[time_zone_tp_cd - 1],
            width: columnWidths[1],
          }, {
            align: 'center',
            text: numberWithComma(bolus_amt || 0),
            width: columnWidths[2],
          }, {
            align: 'center',
            text: numberWithComma(bldsugr || 0),
            width: columnWidths[3],
          }, {
            callback: (cell, options) => {
              const {
                left,
                top,
                width,
                height,
                textWidth,
              } = options;
              const fillColor = ABNORMAL_GLOCOSE_COLORS[cell.abnormal_bldsugr_tp];
              roundedRect(doc, { lineWidth: 1, fillColor }, left + cell.width / 2 + textWidth / 2 + 1, top + (height - 7) / 2, 7, 7, 3.5, 3.5);
            },
            align: 'center',
            text: ABNORMAL_GLOCOSE_TYPES[abnormal_bldsugr_tp],
            width: columnWidths[4],
            abnormal_bldsugr_tp,
          }, {
            align: 'center',
            text: numberWithComma(abnormal_bldsugr_maintain_time),
            width: columnWidths[5],
          }, {
            align: 'center',
            text: before_abnormal_bldsugr_meal_yn ? '○' : '',
            width: columnWidths[6],
          }, {
            align: 'center',
            text: before_abnormal_bldsugr_active_min,
            width: columnWidths[7],
          }]
        });

        const { totalHeight } = await vbox({
          left,
          top,
          width,
          height: 55,
        }, [
          drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
          ...body.map((columns) => {
            return drawTableRow(doc, {}, 27, columns);
          }),
        ]);

        doc.saveGraphicsState();
        doc.setLineWidth(transform(doc, 1));
        doc.setDrawColor('#E2E7EF');
        doc.line(...transform(doc, left, top, left + 1120, top), 'S');
        doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, 1120, totalHeight, 0, 0);
      },
    ]),
    drawSectionBox(doc, data, 1120, 460, T('인슐린 통계'), null, [
      async (left, top, width, height) => {
        const ratio = 1120 / data.insulin_statistics_chart.width;
        const position = transform(doc, left, top, data.insulin_statistics_chart.width * ratio, data.insulin_statistics_chart.height * ratio);
        await addImage(doc, 'insulin_statistics_chart', data.insulin_statistics_chart.url, ...position);
        return roundedRect(doc, {}, left, top, 1120, 308, 0, 0);
      },
      async (left, top, width, height) => {
        // table

        const cellWidth = (1120) / 5;
        const columnWidths = [
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth
        ];
        const headers = [
          T('구분'),
          T('새벽(0-6H)'),
          T('오전(6-12H)'),
          T('오후(12-18H)'),
          T('저녁(18-24H)')
        ].map((text, index) => ({
          text,
          width: columnWidths[index],
        }));

        await vbox({
          left,
          top,
          width,
          height: 55,
        }, [
          drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
          drawTableRow(doc, {}, 27, [
            T('평균 기저 투여량 (U)'),
            ...data.insulin_pattern_time_list.list.map((item) => item.giju_amt.toString()),
          ].map((text, index) => ({
            align: 'center',
            text,
            width: columnWidths[index],
          }))),
          drawTableRow(doc, {}, 27, [
            T('평균 볼루스 투여량 (U)'),
            ...data.insulin_pattern_time_list.list.map((item) => item.bolus_amt.toString()),
          ].map((text, index) => ({
            align: 'center',
            text,
            width: columnWidths[index],
          }))),
        ]);

        doc.saveGraphicsState();
        doc.setLineWidth(transform(doc, 1));
        doc.setDrawColor('#E2E7EF');
        doc.line(...transform(doc, left, top, left + 1120, top), 'S');
        doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, 1120, 102, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}

export async function drawPage10(doc, title, page, data, dataType) {
  await vbox({
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 40,
    left: 0,
    top: 0,
    width: W,
    gap: transform(doc, 40),
  }, [
    drawHeader(doc, data),
    drawPageTitle(doc, title, page, data[dataType], dataType),
    drawSectionBox(doc, data, 1120, 597, T('일별 통계'), null, [
      async (left, top, width, height) => {
        const ratio = 1120 / data.insulin_statistics_day_chart.width;
        const position = transform(doc, left, top, data.insulin_statistics_day_chart.width * ratio, data.insulin_statistics_day_chart.height * ratio);
        await addImage(doc, 'insulin_statistics_day_chart', data.insulin_statistics_day_chart.url, ...position);
        return roundedRect(doc, {}, left, top, 1120, 300, 0, 0);
      },
      async (left, top, width, height) => {
        // table

        const cellWidth = (1120 - 120) / 6;
        const columnWidths = [
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth,
          cellWidth
        ];
        const headers = [
          T('날짜'),
          T('전체 투여량(U)'),
          T('기저 투여량(U)'),
          T('볼루스 투여량(U)'),
          T('볼루스 투여기록(회)'),
          T('볼루스 1회 평균 투여량(U)')
        ].map((text, index) => ({
          text,
          width: columnWidths[index],
        }));

        const body = (data.insulin_stat_list.list || []).map((item, index) => {
          const {
            time,
            insulin_amt,
            giju_amt,
            bolus_amt,
            bolus_cnt,
            once_avg_bolus_amt,
          } = item;

          const luxon = fromFormat(time, 'yyyyMMdd');
          const date = luxon.toFormat("yy.MM.dd");
          const weekday = luxonClone(luxon).weekday;

          return [{
            align: 'center',
            text: T('{0}({1})', date, WEEK_DAY_COLUMNS[weekday - 1]),
            width: columnWidths[0],
          }, {
            align: 'center',
            text: numberWithComma(insulin_amt || 0),
            width: columnWidths[1],
          }, {
            align: 'center',
            text: numberWithComma(giju_amt || 0),
            width: columnWidths[2],
          }, {
            align: 'center',
            text: numberWithComma(bolus_amt || 0),
            width: columnWidths[3],
          }, {
            align: 'center',
            text: numberWithComma(bolus_cnt || 0),
            width: columnWidths[4],
          }, {
            align: 'center',
            text: numberWithComma(once_avg_bolus_amt || 0),
            width: columnWidths[5],
          }]
        });

        const { totalHeight } = await vbox({
          left,
          top,
          width,
          height: 55,
        }, [
          drawTableRow(doc, { fillColor: '#E8ECF2' }, 38, headers),
          ...body.map((columns) => {
            return drawTableRow(doc, {}, 27, columns);
          }),
        ]);

        doc.saveGraphicsState();
        doc.setLineWidth(transform(doc, 1));
        doc.setDrawColor('#E2E7EF');
        doc.line(...transform(doc, left, top, left + 1120, top), 'S');
        doc.line(...transform(doc, left, top + 38, left + 1120, top + 38), 'S');
        doc.restoreGraphicsState();

        return roundedRect(doc, {}, left, top, 1120, totalHeight, 0, 0);
      },
    ]),
    drawFooter(doc, title, page, data),
  ]);
}